import _ from "underscore";
import { AvailabilityEntry } from "../models/AvailabilityEntry";
import { Entry } from "../models/Entry";
import { ClusterKind, TClusterKindValue } from "./EntryConstants";
import { FieldKind } from "./FieldKind";
import { MillenniumDateTime } from "@timeedit/millennium-time";
import MessageDispatcher from "./MessageDispatcher";
import NamedWeek from "./NamedWeek";
import Language from "../lib/Language";
import {
    IUpdateMcFluffyResponse,
    IPartitionObjectParamResponse,
    IGetColorTypesResponse,
    IFindObjectsResponse,
    IGetStartupDataResponse,
    IGetSupportInfoResponse,
    IGetTemplateGroupsResponse,
    IGetPreferencesResponse,
    IGetUserLocaleResponse,
    IExportReservationsResponse,
    IGetLanguagesResponse,
    IGetCreateTypesResponse,
    IGetCurrentDateTimeResponse,
    IFindReservationsListResponse,
    IFindObjectFieldsForReservationListResponse,
    IFindEntriesResponse,
    IGetFieldDefsResponse,
    IFindReservationFieldsResponse,
    IFindFieldsForReservationsResponse,
    ISetPreferencesResponse,
    IFindTypesResponse,
    IGetTimeZonesResponse,
    IShowEnableCustomWeekNamesResponse,
    IGetSumSettingResponse,
    IGetExamSettingsResponse,
    ITargetExternalAppsEnvironmentResponse,
    IHasExternalPermissionsResponse,
    IGetAuthenticatedUserInfoResponse,
    IGetCurrentUserPrefsResponse,
    IGetOtherUserPrefsResponse,
    IGetObjectNamesResponse,
    ILoginWithSSOTokenResponse,
    IGetServerEnvResponse,
    IGetSuggestedObjectsAdvancedResponse,
    IFindFieldsForTypesResponse,
    IGetTypeDefsExtendedResponse,
    IGetUsersResponse,
    IGetRelatedTypesResponse,
    IGetObjectTemplateResponse,
    IGetCreateTypesKindsResponse,
    IExportObjectsResponse,
    IOkToModifyObjectsResponse,
    IGetModifiableObjectFieldsResponse,
    IGetLimitationsResponse,
    IGetOrganizationsResponse,
    IOkToPublishViewResponse,
    IOkToChangeViewsResponse,
    IGetReservationsResponse,
    IOkToSetReservationOrganizationsResponse,
    IGetValidReservationStatusResponse,
    IGetTypesResponse,
    IFindFieldsResponse,
    IGetModifiableReservationFieldsResponse,
    IFindPeriodsResponse,
    IAddReservationsLockResponse,
    IRemoveReservationsLockResponse,
    ICancelReservationsResponse,
    IGetReservationStatusResponse,
    ISetReservationStatusResponse,
    IUseDualSizeSumResponse,
    ICanHaveMemberExceptionObjectsResponse,
    IGetFieldsResponse,
    IGetAllFieldsResponse,
    IGetReservationLayersResponse,
    ICreateReservationLayerResponse,
    IDeleteReservationLayerResponse,
    IGetGroupedEntriesResponse,
    IGetTypeTreeResponse,
    IGetReservableTypesForTypeResponse,
    IGetTypesOnReservationsResponse,
    ISetReservationCapacityResponse,
    IImportObjectsResponse,
    IAddReservationsToReservationGroupResponse,
    ICreateReservationGroupResponse,
    IReserveMcFluffyResponse,
    IGetCustomWeekNamesResponse,
    IGetReservationGroupResponse,
    IGetObjectsResponse,
    IGetOrgInfoResponse,
    IGetReservationsHistoryResponse,
    IFindTagsResponse,
    IGetObjectsOnReservationsResponse,
    IGetReservationsOnLayerResponse,
    IFindReservationsCancelledListResponse,
    IGetOrderRowsResponse,
    IGetPrimaryFieldsResponse,
    IGetMemberTypesForReservationsResponse,
    IGetEmailReservationPrefsResponse,
    ISendEmailReservationResponse,
    IGetAssociatedObjectsResponse,
    IDeleteFromSuggestedObjectsResponse,
    IGetMemberObjectsForReservationsResponse,
    IGetMemberExceptionObjectsResponse,
    ISaveExceptionObjectsResponse,
    IGetSuggestedObjectsSimpleResponse,
    IFindUsersResponse,
    TAddToReservationLayerResult,
    TAddToMcFluffyServerResult,
    ISetPrimaryFieldResponse,
    ILogoutUserResponse,
    IDayNumberFromStringResponse,
    ISetCurrentUserPrefsResponse,
    IDeleteActivityInfoResponse,
    IDeleteObjectsResponse,
    IExportOrdersResponse,
    IFindHPeriodsResponse,
    IGetHPeriodDefsResponse,
    IFindSumSettingsResponse,
    IGetAssociatedTypesResponse,
    IGetReservationCollisionsResponse,
    IGetPaddingRuleNamesResponse,
    IFindOrderRowsListResponse,
    IGetPartitionFieldsResponse,
    ICreateEditableObjectResponse,
    IGetPreferencesMultiResponse,
    ISetOrderRowToMcFluffyResponse,
    ISetSumSettingResponse,
    IDeleteSumSettingResponse,
    IRemoveReservationsFromGroupResponse,
    ISetReservationOrganizationsResponse,
    IAddToReservationLayerResponse,
    IMoveReservationsFromWaitingListResponse,
    ICopyReservationsInTimeResponse,
    IRemoveFromMcFluffyResponse,
    IGetObjectsWithFieldValuesResponse,
    IRestoreReservationsResponse,
    IGroupReservationsResponse,
    IGetPartitionObjectsResponse,
    IGetPartitionMemberTypeResponse,
    IGetReservationConflictsResponse,
    IOkToModifyOrdersResponse,
    IDeleteOrderRowsResponse,
    IImportOrdersResponse,
    IAddTagToReservationsResponse,
    IRemoveTagFromReservationsResponse,
    ICreateTagResponse,
    IDeleteReservationGroupResponse,
    IUpdateReservationGroupResponse,
    IRenameReservationLayerResponse,
    IGetReservationsCancelledResponse,
    IFindNextCreateTypesResponse,
    IDeleteFromReservationLayerResponse,
    IPersistReservationLayerResponse,
    ISetModifiableReservationFieldsResponse,
    IDeleteReservationsCancelledResponse,
    IMoveReservationsInTimeResponse,
    IReplaceObjectsOnReservationsResponse,
    IGetReservationsWithTimeResponse,
    IReservationClusterChangeTimeResponse,
    IAddToMcFluffyResponse,
    ISetToMcFluffyResponse,
    IReplaceInMcFluffyResponse,
    ISetReservationToMcFluffyResponse,
    IMoveReservationsToWaitingListResponse,
    IGetReservationsWithObjectsResponse,
    IOkToCancelReservationsResponse,
    IOkToDeleteReservationsCancelledResponse,
} from "../types/api/APIResponseTypes";

import { TFieldValue } from "../types/components/SplitObjectDialog";

import {
    TEmailReservationPrefs,
    TObject,
    TObjectId,
    TObjectsGetResult,
    TRef,
    TSupportContact,
} from "../types/api/shared";

import {
    TAddToMcFluffyParams,
    TApiErrorCallback,
    TEObject,
    TLanguages,
    TPartitionObjectParams,
    TSetToMcFluffyParams,
    TSuggestedObjectRef,
    TApiSearchQuery,
    TEmailReservationParams,
    TMcFluffyObjectRef,
    TModifiableField,
    TSetPrefValue,
    TSetPrefRef,
    TReplaceInMcFluffyParams,
    TRemoveFromMcFluffyParams,
} from "../types/api/APIParameterTypes";
import {
    TFieldDefClass,
    TFieldvalueClass,
    TTypeNameClass,
    TOrderCreate,
    TReservationDefClass,
    TFieldNameClass,
    TPeriodNameClass,
    TTimeSlotClass,
    TObjectIdClass,
    TReservationIdClass,
    TObjectCreateRawClass,
    TReservationLayerNameClass,
    TReservationStatusClass,
    TReservationCancelledDefClass,
    TReservationModifiedDefClass,
    TTypeIdClass,
    TTypeNodeClass,
    TFieldIdClass,
    THPeriodNameClass,
    TObjectCreateClass,
    TAPIStatusClass,
    TMcFluffyClass,
} from "../types/api/APIClassTypes";
import {
    getPrefsKey,
    parsePrefsValues,
    parsePrefValue,
    toFieldId,
    toMtsList,
} from "./TimeEditAPIUtils";
import { TNewUserArgs } from "../models/User";
import { TReservationCollision } from "../types/components/ReservationTypeList";

type LastFindObject = {
    /** The query the cached answer was found with, compared key by key. */
    query: null | TApiSearchQuery;
    response: null | {
        totalNumber: null | number;
        objects: null | TFoundObject[];
    };
};

let dispatcher: MessageDispatcher = null as unknown as MessageDispatcher;
let logger: { info: (message: string) => void } | null = null;
let types: TTypeNameClass[] = [];

let fields: TFieldNameClass[] = [];
const fieldDefs = {};
const limitCache: {
    [key: string]: {
        [key: string]: {
            value: number;
            description: string;
        };
    };
} = {};
let typeFieldCache = {};
let allFieldsCache = {};
let userSearchFieldCache: null | number[] | object = null;
const templateGroupCache = {};
const MASS_BATCH_SIZE = 50;
const STATUS_BATCH_SIZE = 250;
const GET_OBJECTS_BATCH_SIZE = 1000;
const GET_RESERVATIONS_BATCH_SIZE = 1000;
const EXPORT_RESERVATIONS_BATCH_SIZE = 1000;
const CANCEL_RESERVATIONS_BATCH_SIZE = 1000;
const DELETE_OBJECTS_BATCH_SIZE = 1000;
const FIND_OBJECTS_BATCH_SIZE = 1000;

const DONT_REPEAT_ERRORS = ["-301", "-302", "-303", "-401", "-402", "-403", "-404"];

/* API calls */

const getLanguages = function (callback: (res: TLanguages) => void, stringLanguages = true) {
    dispatcher.send(
        "getLanguages",
        { stringLang: stringLanguages, onlyUsed: false },
        (res: IGetLanguagesResponse) => {
            try {
                const langs: TLanguages = [];
                for (let i = 0; i < res.parameters[0].length; i++) {
                    langs.push({
                        id: res.parameters[0][i],
                        label: res.parameters[1][i],
                    });
                }
                callback(langs);
            } catch (error) {
                console.error("Error in getLanguages", error);
            }
        }
    );
};

const findReservationsList = function (
    query: TApiSearchQuery,
    callback: (res: IFindReservationsListResponse) => void,
    errorCallback: TApiErrorCallback = _.noop
) {
    // Parameter index 5 and 6: bool for success, error message if not success
    dispatcher.send("findReservationsList", query, callback, query.longOperation, errorCallback);
};

const findAllReservationsList = function (
    query: TApiSearchQuery,
    callback: (res: IFindReservationsListResponse["parameters"][0]) => void,
    errorCallback: TApiErrorCallback = _.noop
) {
    findReservationsList(
        query,
        (result) => {
            try {
                const totalNumber = result.parameters[2];
                if (totalNumber > result.parameters[0].length) {
                    const calls: ((done: () => void) => void)[] = [];
                    const totalResult: IFindReservationsListResponse["parameters"][0][] = [
                        result.parameters[0],
                    ];
                    for (
                        let i = result.parameters[0].length;
                        i < totalNumber;
                        i = i + (query.numberOfRows || 100)
                    ) {
                        calls.push((done) => {
                            findReservationsList(
                                Object.assign({}, query, { startRow: i }),
                                (subResult) => {
                                    totalResult.push(subResult.parameters[0]);
                                    done();
                                },
                                (status, message, response) => {
                                    errorCallback(status, message, response);
                                    done();
                                }
                            );
                        });
                    }
                    _.runSync(calls, () => {
                        callback(_.flatten(totalResult));
                    });
                } else {
                    callback(result.parameters[0]);
                }
            } catch (error) {
                console.error("Error in findAllReservationsList", error);
            }
        },
        errorCallback
    );
};

const findReservationsCancelledList = function (
    query: TApiSearchQuery,
    callback: (result: IFindReservationsCancelledListResponse) => void,
    errorCallback = _.noop
) {
    // Parameter index 5 and 6: bool for success, error message if not success
    dispatcher.send(
        "findReservationsCancelledList",
        query,
        callback,
        query.longOperation,
        errorCallback
    );
};

const okToCancelReservations = function (
    query: { reservationIds: number[] },
    callback: (res: { parameters: [TAPIStatusClass[], boolean[]] }) => void
) {
    let fullResult: TAPIStatusClass[] = [];
    let fullEmailResult: boolean[] = [];
    const calls = _.splitArray(query.reservationIds, STATUS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "okToCancelReservations",
            { reservationIds: batch },
            (result: IOkToCancelReservationsResponse) => {
                try {
                    fullResult = fullResult.concat(result.parameters[0]);
                    fullEmailResult = fullEmailResult.concat(result.parameters[1]);
                } catch (error) {
                    console.error("Error in okToCancelReservations", error);
                } finally {
                    done();
                }
            },
            true
        );
    });
    _.runSync(calls, () => {
        callback({ parameters: [fullResult, fullEmailResult] });
    });
};

const okToChangeViews = function (
    viewIds: number,
    callback: (res: IOkToChangeViewsResponse) => void
) {
    dispatcher.send("okToChangeViews", { viewIds }, callback);
};

const okToPublishView = function (
    viewId: number,
    callback: (res: IOkToPublishViewResponse) => void
) {
    dispatcher.send("okToPublishView", { viewId }, callback);
};

const exportOrders = function (orderIds: number[], callback: (orders: TOrderCreate[]) => void) {
    dispatcher.send(
        "exportOrders",
        { orderIds, createObjectsWithNames: true },
        (result: IExportOrdersResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in exportOrders", error);
            }
        }
    );
};

const importOrders = function (
    orders: TOrderCreate[],
    callback: (status: TAPIStatusClass[]) => void
) {
    dispatcher.send("importOrders", { orders }, (result: IImportOrdersResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in importOrders", error);
        }
    });
};

const deleteOrderRows = function (
    orderRow: number[],
    callback: (status: TAPIStatusClass[]) => void
) {
    dispatcher.send(
        "deleteOrderRows",
        {
            orderRowIds: orderRow.map((oId) => ({ class: "orderrowid", id: oId })),
        },
        (result: IDeleteOrderRowsResponse) => {
            callback(result.parameters[0]);
        }
    );
};

const getReservations = function (
    reservationIds: number[],
    callback: (reservations: TReservationDefClass[]) => void,
    errorCallback?: (status: string, message?: unknown, response?: unknown) => void
) {
    let fullResult: TReservationDefClass[] = [];
    let firstError: { status: string; message: unknown; response?: unknown } | null = null;
    const calls = _.splitArray(reservationIds, GET_RESERVATIONS_BATCH_SIZE).map(
        (batch) => (done) => {
            dispatcher.send(
                "getReservations",
                { reservationIds: batch },
                (result: IGetReservationsResponse | Error) => {
                    try {
                        // On HTTP 500/504 the dispatcher invokes the regular callback with an Error
                        if (errorCallback && result instanceof Error) {
                            throw result;
                        }
                        fullResult = fullResult.concat(
                            (result as IGetReservationsResponse).parameters[0]
                        );
                    } catch (error) {
                        console.error("Error in getReservations", error);
                        if (!firstError) {
                            firstError = { status: "error", message: error };
                        }
                    } finally {
                        done();
                    }
                },
                false,
                // Without an errorCallback, keep the dispatcher's default (noop) so existing
                // callers behave exactly as before. With one, done() must still run so the
                // remaining batches drain and runSync completes.
                errorCallback
                    ? (status, message, response) => {
                          if (!firstError) {
                              firstError = { status, message, response };
                          }
                          done();
                      }
                    : undefined
            );
        }
    );
    _.runSync(calls, () => {
        if (errorCallback && firstError) {
            errorCallback(firstError.status, firstError.message, firstError.response);
            return;
        }
        callback(fullResult);
    });
};

const getReservationsByExtid = function (
    /**
     * Extids or internal ids, per element. useExtid asks for extids back rather
     * than declaring what is sent, and the proxy keys each item by its runtime
     * type, so Entry passing numeric ids is fine.
     */
    reservationIds: (number | string)[],
    callback: (reservations: TReservationDefClass[]) => void
) {
    let fullResult = [];
    const calls = _.splitArray(reservationIds, GET_RESERVATIONS_BATCH_SIZE).map(
        (batch) => (done) => {
            dispatcher.send(
                "getReservations",
                { reservationIds: batch, useExtid: true },
                (result) => {
                    try {
                        fullResult = fullResult.concat(result.parameters[0]);
                    } catch (error) {
                        console.error("Error in getReservationsByExtid", error);
                    } finally {
                        done();
                    }
                }
            );
        }
    );
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

const getReservationsCancelled = function (
    reservationIds: number[],
    callback: (reservations: TReservationCancelledDefClass[]) => void
) {
    dispatcher.send(
        "getReservationsCancelled",
        { reservationIds },
        (result: IGetReservationsCancelledResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getReservationsCancelled", error);
            }
        }
    );
};

const getReservationsHistory = function (
    reservationIds: number[],
    callback: (history: TReservationModifiedDefClass[][]) => void
) {
    dispatcher.send(
        "getReservationsHistory",
        { reservationIds },
        (result: IGetReservationsHistoryResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getReservationsHistory", error);
            }
        }
    );
};

const exportReservations = function (
    reservationIds: number[],
    includeExtraInfo: boolean,
    callback: (res: IExportReservationsResponse["parameters"][0]) => void
) {
    let fullResult: IExportReservationsResponse["parameters"][0] = [];
    const calls = _.splitArray(reservationIds, EXPORT_RESERVATIONS_BATCH_SIZE).map(
        (batch) => (done) => {
            dispatcher.send(
                "exportReservations",
                { reservationIds: batch, includeExtraInfo, useExtid: true },
                (result: IExportReservationsResponse) => {
                    try {
                        fullResult = fullResult.concat(result.parameters[0]);
                    } catch (error) {
                        console.error("Error in exportReservations", error);
                    } finally {
                        done();
                    }
                }
            );
        }
    );
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

const getModifiableReservationFields = function (reservationIds: number[], callback) {
    dispatcher.send(
        "getModifiableReservationFields",
        { reservationIds },
        (result: IGetModifiableReservationFieldsResponse) => {
            try {
                callback(result.parameters[0], result.parameters[1]);
            } catch (error) {
                console.error("Error in getModifiableReservationFields", error);
            }
        }
    );
};

const setModifiableReservationFields = function (
    reservationIds: number[],
    modifiableFields: TModifiableField[],
    callback: (status: TAPIStatusClass[]) => void
) {
    dispatcher.send(
        "setModifiableReservationFields",
        { reservationIds, fields: modifiableFields },
        (result: ISetModifiableReservationFieldsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in setModifiableReservationFields", error);
            }
        }
    );
};

const getAssociatedTypes = function (typeId: number, callback: (types: TTypeIdClass[]) => void) {
    dispatcher.send(
        "getAssociatedTypes",
        { type: typeId },
        (result: IGetAssociatedTypesResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getAssociatedTypes", error);
            }
        }
    );
};

const getAssociatedObjects = function (
    objects: number[],
    type: number,
    callback: (objects: TObjectIdClass[]) => void
) {
    dispatcher.send(
        "getAssociatedObjects",
        { objects, type },
        (result: IGetAssociatedObjectsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getAssociatedObjects", error);
            }
        }
    );
};

const getSuggestedObjectsSimple = function (
    selectedObject: TSuggestedObjectRef,
    resultTypeId: number | undefined,
    allObjectIds: number[] | undefined,
    beginTime: number | undefined,
    endTime: number | undefined,
    callback: (res: IGetSuggestedObjectsSimpleResponse["parameters"]) => void,
    signal?: AbortSignal,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "getSuggestedObjectsSimple",
        { selectedObject, resultType: resultTypeId, allObjectIds, beginTime, endTime },
        (result: IGetSuggestedObjectsSimpleResponse) => {
            // On HTTP 500/504 the dispatcher invokes the regular callback with an Error
            if (errorCallback && result instanceof Error) {
                errorCallback("error", result);
                return;
            }
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in getSuggestedObjectsSimple", error);
            }
        },
        false,
        errorCallback || _.noop,
        signal
    );
};

const getSuggestedObjectsAdvanced = function (
    selectedObject: TSuggestedObjectRef,
    otherObjects: TSuggestedObjectRef[] | undefined,
    isReservationMode: boolean,
    templateGroupId: number | null | undefined,
    beginTime: number,
    endTime: number,
    skipLastReserved: boolean | undefined,
    callback: (res: IGetSuggestedObjectsAdvancedResponse["parameters"]) => void,
    signal?: AbortSignal
) {
    dispatcher.send(
        "getSuggestedObjectsAdvanced",
        {
            selectedObject,
            otherObjects,
            isReservationMode,
            templateGroupId,
            beginTime: beginTime || 0,
            endTime: endTime || 0,
            allObjectIds: [],
            reservationId: 0,
            skipLastReserved,
        },
        (result: IGetSuggestedObjectsAdvancedResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in getSuggestedObjectsAdvanced", error);
            }
        },
        false,
        _.noop,
        signal
    );
};

const getSuggestedObjectsAdvanced2 = function (
    selectedObject: TSuggestedObjectRef,
    otherObjects: TSuggestedObjectRef[],
    isReservationMode: boolean,
    templateGroupId: number | null | undefined,
    beginTime: number,
    endTime: number,
    allObjectIds: number[],
    reservationId: number,
    skipLastReserved: boolean | undefined,
    callback: (res: IGetSuggestedObjectsAdvancedResponse["parameters"]) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "getSuggestedObjectsAdvanced",
        {
            selectedObject,
            otherObjects,
            isReservationMode,
            templateGroupId,
            beginTime,
            endTime,
            allObjectIds,
            reservationId,
            skipLastReserved,
        },
        (result: IGetSuggestedObjectsAdvancedResponse) => {
            // On HTTP 500/504 the dispatcher invokes the regular callback with an Error
            if (errorCallback && result instanceof Error) {
                errorCallback("error", result);
                return;
            }
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in getSuggestedObjectsAdvanced2", error);
            }
        },
        false,
        errorCallback
    );
};

const deleteFromSuggestedObjects = function (
    objectId: number | undefined,
    callback: (result: IDeleteFromSuggestedObjectsResponse["parameters"]) => void
) {
    dispatcher.send(
        "deleteFromSuggestedObjects",
        { objectId },
        (result: IDeleteFromSuggestedObjectsResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in deleteFromSuggestedObjects", error);
            }
        }
    );
};

const findAllSearchObjects = function (
    alreadyFound: TObject[],
    totalNumber: number,
    query: TApiSearchQuery,
    callback: (objects: TObject[]) => void
) {
    const query2 = { ...query };
    query2.startRow = alreadyFound.length;
    query2.useExtid = true;
    dispatcher.send("findObjects", query2, (result2: IFindObjectsResponse) => {
        try {
            const totalFound = [...alreadyFound, ...result2.parameters[0]];
            if (totalFound.length === totalNumber) {
                callback(totalFound);
            } else {
                findAllSearchObjects(totalFound, totalNumber, query, callback);
            }
        } catch (error) {
            console.error("Error in findAllSearchObjects", error);
        }
    });
};

const finishFindObjects = function (
    foundObjects: TObject[],
    totalNumber: number,
    query: TApiSearchQuery,
    callback: (objects: TFoundObject[], count: number | null | undefined) => void
) {
    let primaryFieldIndex = (query.returnFields ?? []).findIndex(
        (returnField) => returnField === query.primaryField
    );
    if (primaryFieldIndex === -1) {
        primaryFieldIndex = 0;
    }

    const objects = foundObjects.map((element) => {
        return {
            ...element,
            name: element.fields[primaryFieldIndex]?.values?.[0] ?? "",
            // findObjects has already refused anything but a plain id above.
            typeId: query.type as number,
            reservationKind: element.reservationkind || 0,
        };
    });
    lastFindObjects = {
        query,
        response: {
            objects,
            totalNumber,
        },
    };
    _findObjectsCacheTimeout = window.setTimeout(() => {
        lastFindObjects = { query: null, response: null };
    }, 300000);
    callback(objects, totalNumber);
};

let lastFindObjects: LastFindObject = {
    query: null,
    response: { totalNumber: null, objects: null },
};
let _findObjectsCacheTimeout: number = 0; // does it matter if this is set to 0 by default?????

let errorFindObjectsQueries: TApiSearchQuery[] = [];
let errorFindObjectsMessages: string[] = [];

/**
 * What finishFindObjects hands back: the object as the server sent it, plus the
 * primary field's value as `name` and the query's type.
 */
type TFoundObject = TObject & {
    name: string;
    typeId: number;
    reservationKind: number;
};

const findObjects = function (
    query: TApiSearchQuery,
    callback: (objects: TFoundObject[], count: number | null | undefined) => void,
    signal?: AbortSignal
) {
    // We always want the type to be a pure ID. Move away from passing a whole TE JSON object around.
    if (typeof query.type === "object" && query.type !== null) {
        throw new Error(
            `Invoked findObjects with an object instead of a type ID ${JSON.stringify(query)}`
        );
    }

    query.returnFields = query.returnFields || [];
    if (query.returnFields.length === 0 && query.generalSearchFields) {
        // Fields should also become pure IDs.
        query.returnFields = query.generalSearchFields.map(toFieldId);
    } else if (query.returnFields.length > 0) {
        query.returnFields = query.returnFields.map(toFieldId);
    }

    if (lastFindObjects.response?.objects && _.isEqual(query, lastFindObjects.query)) {
        callback(lastFindObjects.response.objects, lastFindObjects.response.totalNumber);
        return;
    }
    clearTimeout(_findObjectsCacheTimeout);

    if (query.searchObjects && query.searchObjects.length) {
        query.numberOfRows = Math.min(
            Math.max(query.numberOfRows || 0, query.searchObjects.length),
            FIND_OBJECTS_BATCH_SIZE
        );
    }

    let index = errorFindObjectsQueries.findIndex((errorQuery) => _.isEqual(query, errorQuery));
    if (index > -1) {
        if (logger) {
            let message = errorFindObjectsMessages[index];
            // Don't report too many object matching a basic query
            // The best cause of action is to enter a longer query
            if (message.indexOf("-1054") === -1) {
                logger.info(message);
            }
        }
        callback([], 0);
        return;
    }

    dispatcher.send(
        "findObjects",
        query,
        (result: IFindObjectsResponse) => {
            try {
                const foundObjects = result.parameters[0];
                // Parameter 1 is a datetime object
                const totalNumber = result.parameters[2];

                if (
                    query.searchObjects &&
                    query.searchObjects.length > 0 &&
                    foundObjects.length < totalNumber
                ) {
                    findAllSearchObjects(
                        foundObjects,
                        totalNumber,
                        { ...query, returnRowsLimit: false },
                        (allFound) => {
                            finishFindObjects(allFound, totalNumber, query, callback);
                        }
                    );
                } else {
                    finishFindObjects(foundObjects, totalNumber, query, callback);
                }
            } catch (error) {
                console.error("Error in findObjects", error);
            }
        },
        false,
        (errorType, message, response) => {
            if (logger) {
                let message = response;
                if (
                    response &&
                    DONT_REPEAT_ERRORS.some((errorCode) => response.indexOf(errorCode) !== -1)
                ) {
                    if (response.indexOf("-302") > -1) {
                        message = Language.get("nc_too_many_exact_search_fields");
                    }

                    errorFindObjectsQueries.push({ ...query });
                    errorFindObjectsMessages.push(message);
                }
                // Don't report too many object matching a basic query
                // The best cause of action is to enter a longer query
                if (response.indexOf("-1054") === -1) {
                    logger.info(message);
                }
            }
        },
        signal
    );
};

const exportObjects = function (
    objectIds: number[],
    callback: (res: IExportObjectsResponse["parameters"][0]) => void
) {
    if (!objectIds || objectIds.length === 0) {
        callback([]);
        return;
    }
    dispatcher.send(
        "exportObjects",
        { objectIds, useExtid: true },
        (result: IExportObjectsResponse) => {
            try {
                result.parameters[0].forEach((obj) => {
                    if (obj.members) {
                        obj.members = obj.members.map((member) => {
                            if (member.object) {
                                return {
                                    begin: new MillenniumDateTime(member.begin ?? 0),
                                    end: new MillenniumDateTime(member.end ?? 0),
                                    object: member.object,
                                };
                            }
                            return member;
                        });
                    }
                });
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in exportObjects", error);
            }
        },
        true
    );
};

const exportObjectsBatch = function (objectIds: number[], callback) {
    if (!objectIds || objectIds.length === 0) {
        callback([]);
        return;
    }
    const fullResult: object[] = [];
    const calls = _.splitArray(objectIds, GET_OBJECTS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "exportObjects",
            { objectIds: batch, useExtid: true },
            (result) => {
                try {
                    result.parameters[0].forEach((obj) => {
                        if (obj.members) {
                            obj.members = obj.members.map((member) => {
                                if (member.object) {
                                    return {
                                        begin: new MillenniumDateTime(member.begin),
                                        end: new MillenniumDateTime(member.end),
                                        object: member.object,
                                    };
                                }
                                return member;
                            });
                        }
                        fullResult.push(obj);
                    });
                } catch (error) {
                    console.error("Error in exportObjectsBatch", error);
                } finally {
                    done();
                }
            },
            true
        );
    });
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

const importObjects = function (
    objects: TObjectCreateClass[],
    addUserOrganization: boolean,
    /** Only "ignore" is passed today; the server takes a mode name. */
    mode: string | undefined,
    callback: (status: TAPIStatusClass[]) => void
) {
    let params = { objects, addUserOrganization, mode };
    dispatcher.send(
        "importObjects",
        params,
        (result: IImportObjectsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in importObjects", error);
            }
        },
        true
    );
};

const importObjectsBatch = function (
    objects: TObjectCreateClass[],
    addUserOrganization: boolean,
    /** Only "ignore" is passed today; the server takes a mode name. */
    mode: string | undefined,
    callback: (status: TAPIStatusClass[]) => void
) {
    let params = { objects, addUserOrganization, mode };
    let fullResult = [];
    const calls = _.splitArray(objects, GET_OBJECTS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "importObjects",
            { objects: batch, addUserOrganization, mode },
            (result) => {
                try {
                    fullResult = fullResult.concat(result.parameters[0]);
                } catch (error) {
                    console.error("Error in importObjectsBatch", error);
                } finally {
                    done();
                }
            },
            true
        );
    });
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

const createEditableObject = function (
    typeIds: number[],
    objectId: number,
    callback: (object: TObjectCreateRawClass) => void
) {
    const params = {
        typeIds,
        objectId,
    };
    dispatcher.send("createEditableObject", params, (result: ICreateEditableObjectResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in createEditableObject", error);
        }
    });
};

const deleteObjects = function (
    objectIds: number[],
    inactivate: boolean,
    callback: (statuses: (TAPIStatusClass | null)[]) => void
) {
    let fullResult: (TAPIStatusClass | null)[] = [];
    const calls = _.splitArray(objectIds, DELETE_OBJECTS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "deleteObjects",
            { objectIds: batch, inactivate },
            (result: IDeleteObjectsResponse) => {
                try {
                    fullResult = fullResult.concat(result.parameters[0]);
                } catch (error) {
                    console.error("Error in deleteObjects", error);
                } finally {
                    done();
                }
            }
        );
    });
    _.runSync(calls, () => {
        callback(_.flatten(fullResult, 1));
    });
};

const getTypeDefsExtended = function (
    typeIds: number[],
    callback: (res: IGetTypeDefsExtendedResponse["parameters"][0]) => void,
    signal?: AbortSignal
) {
    if (typeIds.length === 0) {
        callback([]);
        return;
    }
    dispatcher.send(
        "getTypeDefsExtended",
        { types: typeIds },
        (result: IGetTypeDefsExtendedResponse) => {
            // Works around a server bug before c72624d8e1507b436474e3fbafe8b17077a0a7f5
            try {
                if (
                    typeIds.length === 1 &&
                    result.parameters[0].length === 2 &&
                    result.parameters[0][0] === null
                ) {
                    callback([result.parameters[0][1]]);
                    return;
                }
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getTypeDefsExtended", error);
                callback([]);
            }
        },
        false,
        _.noop,
        signal
    );
};

const getTypes = function (
    typeIds: number[],
    ignoreAlias: boolean,
    callback: (types: TTypeNameClass[]) => void
) {
    if (typeIds.length === 0) {
        callback([]);
        return;
    }
    dispatcher.send("getTypes", { types: typeIds, ignoreAlias }, (result: IGetTypesResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getTypes", error);
            callback([]);
        }
    });
};

let _namedTypesCache = {};

const getTypesByExtid = function (
    typeIds: string[],
    ignoreAlias: boolean,
    callback: (types: TTypeNameClass[]) => void
) {
    if (typeIds.length === 0) {
        callback([]);
        return;
    }
    let foundTypes: TTypeNameClass[] = [];
    typeIds.forEach((typeId, index) => {
        if (_namedTypesCache[typeId]) {
            foundTypes[index] = _namedTypesCache[typeId];
        }
    });
    let soughtTypes = typeIds.filter((typeId) => _namedTypesCache[typeId] === undefined);
    if (soughtTypes.length === 0) {
        callback(foundTypes);
        return;
    }
    dispatcher.send(
        "getTypes",
        { types: soughtTypes, ignoreAlias, useExtid: true },
        (result: IGetTypesResponse) => {
            try {
                const newTypes = result.parameters[0];
                newTypes.forEach((newType) => {
                    _namedTypesCache[newType.extid] = newType;
                    typeIds.forEach((typeId, index) => {
                        if (typeId === newType.extid) {
                            foundTypes[index] = newType;
                        }
                    });
                });
                callback(foundTypes);
            } catch (error) {
                console.error("Error in getTypesByExtid", error);
                callback([]);
            }
        }
    );
};

const getRelatedTypes = function (
    types: number[],
    hasMembers: boolean,
    hasRelated: boolean,
    hasAvailabilityRelated: boolean,
    hasOptionalRelated: boolean,
    callback: (res: IGetRelatedTypesResponse["parameters"]) => void
) {
    const params = {
        types,
        hasMembers,
        hasRelated,
        hasAvailabilityRelated,
        hasOptionalRelated,
    };
    dispatcher.send("getRelatedTypes", params, (result: IGetRelatedTypesResponse) => {
        try {
            callback(result.parameters);
        } catch (error) {
            console.error("Error in getRelatedTypes", error);
        }
    });
};

const getCreateTypes = function (
    typeIds: number[],
    callback: (res: IGetCreateTypesResponse["parameters"][0]) => void
) {
    dispatcher.send("getCreateTypes", { types: typeIds }, (result: IGetCreateTypesResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getCreateTypes", error);
        }
    });
};

const getCreateTypesKinds = function (
    typeIds: number[],
    callback: (res: IGetCreateTypesKindsResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "getCreateTypesKinds",
        { types: typeIds },
        (result: IGetCreateTypesKindsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getCreateTypesKinds", error);
            }
        }
    );
};

const findNextCreateTypes = function (
    typeIds: number[],
    callback: (types: TTypeNameClass[]) => void
) {
    dispatcher.send(
        "findNextCreateTypes",
        { types: typeIds },
        (result: IFindNextCreateTypesResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in findNextCreateTypes", error);
            }
        }
    );
};

const getTypeTree = function (callback: (tree: TTypeNodeClass) => void) {
    dispatcher.send("getTypeTree", {}, (result: IGetTypeTreeResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getTypeTree", error);
        }
    });
};

// beginTime, endTime, fluffy, objectLists
const findEntries = function (
    query: TApiSearchQuery,
    callback: (
        entries: Entry[],
        availability: AvailabilityEntry[],
        isAbsoluteAvailability: boolean | null,
        summaries: IFindEntriesResponse["parameters"][3]
    ) => void
) {
    query.beginTime = toMtsList(query.beginTime);
    query.endTime = toMtsList(query.endTime);

    dispatcher.send("findEntries", query, (response: IFindEntriesResponse) => {
        try {
            const entries: Entry[] = response.parameters[0].map((item) => Entry.create(item));
            let availability: AvailabilityEntry[] = (response.parameters[1] ||
                []) as unknown as AvailabilityEntry[];
            const isAbsoluteAvailability = response.parameters[2];
            availability = availability.map((item) => AvailabilityEntry.create(item));
            const summaries = response.parameters[3];
            callback(entries, availability, isAbsoluteAvailability, summaries);
        } catch (error) {
            console.error("Error in findEntries", error);
        }
    });
};

const findAliasTypes = function (callback: (res: TTypeNameClass[]) => void) {
    dispatcher.send("findTypes", { searchString: "", ignoreAlias: false }, (result) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in findAliasTypes", error);
        }
    });
};

const findTypes = function (callback: (res: TTypeNameClass[]) => void) {
    if (types && types.length > 0) {
        callback(types);
    } else {
        dispatcher.send(
            "findTypes",
            { searchString: "", ignoreAlias: true },
            (result: IFindTypesResponse) => {
                try {
                    types = result.parameters[0];
                    callback(types);
                } catch (error) {
                    console.error("Error in findTypes", error);
                }
            }
        );
    }
};

const findFields = function (callback: (res: TFieldNameClass[]) => void, useCache = true) {
    if (fields && fields.length > 0 && useCache) {
        callback(fields);
    } else {
        dispatcher.send("findFields", { searchString: "" }, (result: IFindFieldsResponse) => {
            try {
                fields = result.parameters[0]; // Cache updates even if useCache is false, this is intentional
                callback(fields);
            } catch (error) {
                console.error("Error in findFields", error);
            }
        });
    }
};

const getAllFields = function (type: number, callback: (fields: TFieldIdClass[]) => void) {
    const key = String(type);
    if (allFieldsCache[key]) {
        callback(allFieldsCache[key]);
    } else {
        dispatcher.send("getAllFields", { type }, (result: IGetAllFieldsResponse) => {
            try {
                allFieldsCache[key] = result.parameters[0];
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getAllFields", error);
            }
        });
    }
};

const findReservationFields = (callback: (res: TFieldDefClass[]) => void) => {
    dispatcher.send("findReservationFields", {}, (result: IFindReservationFieldsResponse) => {
        try {
            getFieldDefs(
                result.parameters[0].map((res) => res.id),
                undefined,
                (resResult) => {
                    callback(resResult);
                }
            );
        } catch (error) {
            console.error("Error in findReservationFields", error);
        }
    });
};

const getFieldsForReservationLists = (callback: (res: TFieldDefClass[]) => void) => {
    dispatcher.send("findReservationFields", {}, (result: IFindReservationFieldsResponse) => {
        try {
            getFieldDefs(
                result.parameters[0].map((res) => res.id),
                undefined,
                (resResult) => {
                    callback(resResult.filter((field) => field.listable === true));
                }
            );
        } catch (error) {
            console.error("Error in getFieldsForReservationLists", error);
        }
    });
};

const findFieldsForTypes = function (
    types: number[],
    includeChildTypeFields: boolean,
    primaryFieldsOnly: boolean,
    callback: (res: IFindFieldsForTypesResponse["parameters"][0]) => void
) {
    const params = { types, includeChildTypeFields, primaryFieldsOnly };
    const key = JSON.stringify(params);
    if (typeFieldCache[key]) {
        callback(typeFieldCache[key]);
    } else {
        dispatcher.send("findFieldsForTypes", params, (result: IFindFieldsForTypesResponse) => {
            try {
                typeFieldCache[key] = result.parameters[0];
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in findFieldsForTypes", error);
            }
        });
    }
};

const findFieldsForReservations = function (
    callback: (res: IFindFieldsForReservationsResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "findFieldsForReservations",
        {},
        (result: IFindFieldsForReservationsResponse) => {
            try {
                return callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in findFieldsForReservations", error);
                return;
            }
        }
    );
};

const getFieldDefs = function (
    fieldIds: number[],
    /** A type id. tecore-server wraps it as { class: "typeid", id: type }. */
    type: number | undefined,
    callback?: (res: IGetFieldDefsResponse["parameters"][0]) => void,
    useCache = true
) {
    const key = `${type !== undefined ? type : "noType"};${fieldIds.join(":")}`;
    if (useCache && fieldDefs[key]) {
        callback?.(JSON.parse(JSON.stringify(fieldDefs[key])));
    } else {
        dispatcher.send(
            "getFieldDefs",
            { ids: fieldIds, type },
            (result: IGetFieldDefsResponse) => {
                try {
                    let defs = result.parameters[0];
                    let titleTypes = defs
                        .map((def) => def.title_type)
                        .filter(
                            (type): type is NonNullable<TFieldDefClass["title_type"]> =>
                                type !== undefined
                        );
                    if (titleTypes.length > 0) {
                        getTypes(
                            titleTypes.map((type) => type.id),
                            false,
                            (typeResult) => {
                                defs.forEach((fieldDef) => {
                                    if (fieldDef.title_type) {
                                        fieldDef.title_type = typeResult.find(
                                            (type) => type.id === fieldDef.title_type!.id
                                        );
                                    }
                                });
                                fieldDefs[key] = defs;
                                callback?.(defs);
                            }
                        );
                    } else {
                        fieldDefs[key] = defs;
                        callback?.(defs);
                    }
                } catch (error) {
                    console.error("Error in getFieldDefs", error);
                }
            }
        );
    }
};

const getFieldDefsForType = function (
    type: number,
    includeChildTypeFields = true,
    callback?: (res: TFieldDefClass[]) => void
) {
    findFieldsForTypes([type], includeChildTypeFields, false, (result) => {
        getFieldDefs(
            result.map((field) => field.id),
            type,
            (defResult) => callback?.(defResult)
        );
    });
};

const getPrimaryFieldDefsForType = function (
    type: number,
    callback?: (res: TFieldDefClass[]) => void
) {
    findFieldsForTypes([type], false, true, (result) => {
        getFieldDefs(
            result.map((field) => field.id),
            type,
            (defResult) => callback?.(defResult)
        );
    });
};

const getFieldDefsForReservations = function (callback?: (res: TFieldDefClass[]) => void) {
    findFieldsForReservations((result) => {
        getFieldDefs(
            result.map((field) => field.id),
            undefined,
            (defResult) => callback?.(defResult)
        );
    });
};

const getAllFieldDefs = function (type: number, callback?: (res: TFieldDefClass[]) => void) {
    getAllFields(type, (result) => {
        getFieldDefs(
            result.map((field) => field.id),
            undefined,
            (defResult) => callback?.(defResult)
        );
    });
};

const findCategories = function (typeId: number, callback: (res: TFieldDefClass[]) => void) {
    getAllFieldDefs(typeId, (result) => {
        callback(_.filter(result, (field) => field.kind === FieldKind.CATEGORY));
    });
};

const getFields = function (fieldIds: (number | string)[], callback) {
    if (fieldIds.length === 0) {
        callback([]);
        return;
    }
    dispatcher.send(
        "getFields",
        { ids: fieldIds, useExtid: true },
        (result: IGetFieldsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getFields", error);
                callback([]);
            }
        }
    );
};

let _extidFieldsCache = {};

const getFieldsByExtid = function (fieldIds: string[], callback) {
    if (fieldIds.length === 0) {
        callback([]);
        return;
    }
    const foundFields: object[] = [];
    fieldIds.forEach((fieldId, index) => {
        if (_extidFieldsCache[fieldId]) {
            foundFields[index] = _extidFieldsCache[fieldId];
        }
    });
    let ids = fieldIds.filter((fieldId) => _extidFieldsCache[fieldId] === undefined);
    if (ids.length === 0) {
        callback(foundFields);
        return;
    }
    dispatcher.send("getFields", { ids, useExtid: true }, (result) => {
        try {
            result.parameters[0].forEach((field) => {
                _extidFieldsCache[field.extid] = field;
                fieldIds.forEach((fieldId, index) => {
                    if (fieldId === field.extid) {
                        foundFields[index] = field;
                    }
                });
            });
            callback(foundFields);
        } catch (error) {
            console.error("Error in getFieldsByExtid", error);
            callback([]);
        }
    });
};

const setPrimaryField = function (
    type: number,
    field: number,
    callback: (res: ISetPrimaryFieldResponse) => void
) {
    dispatcher.send("setPrimaryField", { type, field }, callback);
};

const getPrimaryFields = function (type: number, callback: (fields: TFieldIdClass[]) => void) {
    dispatcher.send("getPrimaryFields", { type }, (result: IGetPrimaryFieldsResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getPrimaryFields", error);
        }
    });
};

const getObjectsByExtid = function (
    objectIds: number[],
    callback: (res: TObjectsGetResult[]) => void
) {
    const fullResult: TObjectsGetResult[] = [];
    if (objectIds.length === 0) {
        callback(fullResult);
        return;
    }
    const calls = _.splitArray(objectIds, GET_OBJECTS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send("getObjects", { ids: batch, useExtid: true }, (result) => {
            try {
                _.each(result.parameters[0], (element) => {
                    fullResult.push({
                        id: element.id,
                        fields: element.fields,
                        reservationKind: element.reservationkind,
                        color: element.color,
                        extid: element.extid,
                    });
                });
            } catch (error) {
                console.error("Error in getObjectsByExtid", error);
            } finally {
                done();
            }
        });
    });
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

const getObjects = function (
    objectIds: number[],
    callback: (res: TObjectsGetResult[]) => void,
    signal?: AbortSignal
) {
    const fullResult: TObjectsGetResult[] = [];
    if (objectIds.length === 0) {
        callback(fullResult);
        return;
    }
    const calls = _.splitArray(objectIds, GET_OBJECTS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "getObjects",
            { ids: batch },
            (result: IGetObjectsResponse) => {
                try {
                    _.each(result.parameters[0], (element) => {
                        fullResult.push({
                            id: element.id,
                            fields: element.fields,
                            reservationKind: element.reservationkind,
                            color: element.color,
                            extid: element.extid,
                        });
                    });
                } catch (error) {
                    console.error("Error in getObjects", error);
                } finally {
                    done();
                }
            },
            false,
            _.noop,
            signal
        );
    });
    _.runSync(calls, () => {
        if (signal?.aborted) {
            return;
        }
        callback(fullResult);
    });
};

export type TGetObjectNamesRes = {
    id: number;
    fields: TFieldvalueClass[];
};
const getObjectNames = function (
    /** Bare ids, or id/type pairs from the conflict tooltip. */
    objectIds: (number | { id: number; type: number })[],
    activeOnly: boolean,
    callback: (res: TGetObjectNamesRes[]) => void
) {
    const fullResult: TGetObjectNamesRes[] = [];
    if (objectIds.length === 0) {
        callback(fullResult);
        return;
    }
    const calls = _.splitArray(objectIds, GET_OBJECTS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "getObjectNames",
            { ids: batch, activeOnly },
            (result: IGetObjectNamesResponse) => {
                try {
                    _.each(result.parameters[0], (element) => {
                        if (element !== null) {
                            fullResult.push({
                                id: element.id,
                                fields: element.fields,
                            });
                        }
                    });
                } catch (error) {
                    console.error("Error in getObjectNames", error);
                } finally {
                    done();
                }
            }
        );
    });
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

let _namedObjectsCache = {};

const clearAmCaches = () => {
    _namedObjectsCache = {};
    _namedTypesCache = {};
    _extidFieldsCache = {};
};

const getObjectNamesByExtid = function (
    /** Extids, or objects carrying one; the filter below accepts either. */
    objectIds: (string | { id: string; type?: string })[],
    activeOnly: boolean,
    callback: (objects: TEObject[]) => void
) {
    const fullResult: TEObject[] = [];
    if (objectIds.length === 0) {
        callback(fullResult);
        return;
    }
    // Pick any existing objects from the cache
    // Make calls for the rest
    let objects = objectIds
        .filter((object) => (typeof object === "object" ? object.id : object))
        .map((objOrStr) => ({
            extId: typeof objOrStr === "object" ? objOrStr.id : objOrStr,
            type: typeof objOrStr === "object" ? objOrStr.type ?? "" : "",
        }));
    objects.forEach((obj, index) => {
        if (_namedObjectsCache[obj.extId]) {
            fullResult[index] = _namedObjectsCache[obj.extId];
        }
    });
    let searchObjects = objects.filter((obj) => _namedObjectsCache[obj.extId] === undefined);
    if (searchObjects.length === 0) {
        callback(fullResult);
        return;
    }
    const calls = _.splitArray(
        searchObjects.filter((objId) => objId),
        GET_OBJECTS_BATCH_SIZE
    ).map((batch) => (done) => {
        dispatcher.send("getObjectNames", { ids: batch, activeOnly, useExtid: true }, (result) => {
            try {
                _.each(result.parameters[0], (element) => {
                    if (element !== null) {
                        objects.forEach((obj, index) => {
                            if (obj.extId === element.extid) {
                                fullResult[index] = {
                                    id: element.id,
                                    extid: element.extid,
                                    fields: element.fields,
                                };
                            }
                        });
                    }
                });
            } catch (error) {
                console.error("Error in getObjectNamesByExtid", error);
            } finally {
                done();
            }
        });
    });
    _.runSync(calls, () => {
        // Update cache with fetched objects
        fullResult.forEach((object) => {
            _namedObjectsCache[object.extid] = object;
        });
        callback(fullResult);
    });
};

const getObjectsLimited = function (
    objectIds: number[],
    callback: (res: TObjectsGetResult[]) => void
) {
    if (objectIds.length > 1000) {
        const subset = objectIds.slice(0, 999);
        getObjects(subset, callback);
    } else {
        getObjects(objectIds, callback);
    }
};

const getObjectsWithFieldValues = function (
    typeId: number,
    fieldId: number,
    values: string[],
    callback: (res: TObjectIdClass[]) => void,
    signal: AbortSignal
) {
    dispatcher.send(
        "getObjectsWithFieldValues",
        {
            typeId: { class: "typeid", id: typeId },
            fieldId: { class: "fieldid", id: fieldId },
            values,
        },
        (result: IGetObjectsWithFieldValuesResponse) => {
            callback(result.parameters[0]);
        },
        false,
        (errorType, message, response) => {
            // Currently only used for pre-save checks, so no need to alert the user on errors
            console.error("Error in getObjectsWithFieldValues", errorType, message, response);
        },
        signal
    );
};

const getTemplateGroups = function (
    templateKind,
    callback: (res: IGetTemplateGroupsResponse) => void
) {
    if (templateGroupCache.hasOwnProperty(templateKind)) {
        callback(templateGroupCache[templateKind]);
        return;
    }

    dispatcher.send(
        "getTemplateGroups",
        { templateKind, useExtid: true },
        (result: IGetTemplateGroupsResponse) => {
            templateGroupCache[templateKind] = result;
            callback(result);
        }
    );
};

// Sorting: USER_SORT_ID = 0, USER_SORT_LOGIN, USER_SORT_LAST_NAME, USER_SORT_FIRST_NAME, USER_SORT_LAST_LOGIN, USER_SORT_MODIFIED
const findUsers = function (
    query: TApiSearchQuery,
    callback: (result: IFindUsersResponse) => void = (result) => {
        console.error(result);
    }
) {
    // authServer = "timeedit", loginName = null, name = null, email = null, allowedToLoginOnly = true, sortOrder = 0, startRow = 0, numberOfRows = 20
    query.authServer = query.authServer || "timeedit";
    query.sortOrder = query.sortOrder || 0;
    query.startRow = query.startRow || 0;
    query.numberOfRows = query.numberOfRows || 20;
    dispatcher.send("findUsers", query, callback);
};

const getUsers = function (
    userIds: number[],
    callback: (res: IGetUsersResponse["parameters"][0]) => void
) {
    dispatcher.send("getUsers", { userIds }, (result: IGetUsersResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getUsers", error);
        }
    });
};

const updateMcFluffy = function (
    mcFluffy: TMcFluffyClass | null,
    callback: (res: IUpdateMcFluffyResponse) => void
) {
    dispatcher.send("updateMcFluffy", { mcFluffy }, callback);
};

const addToMcFluffy = function (
    mcFluffy: TMcFluffyClass,
    typeId: number,
    objectId: number,
    callback: (res: IAddToMcFluffyResponse) => void
) {
    const parameters: TAddToMcFluffyParams = { mcFluffy, typeId, objectId };
    if (objectId) {
        parameters.objectId = objectId;
    }
    dispatcher.send("addToMcFluffy", parameters, callback);
};

const addToMcFluffyMulti = function (
    mcFluffy: TMcFluffyClass,
    typeId: number,
    objectIds: number[],
    callback: (res: IAddToMcFluffyResponse) => void
) {
    const parameters = { mcFluffy, typeId, objectIds };
    dispatcher.send("addToMcFluffy", parameters, callback);
};

const addToMcFluffyServer = function (
    mcFluffy: TMcFluffyClass,
    typesWithObjects: Record<string, TSuggestedObjectRef[]>,
    callback: (result: TAddToMcFluffyServerResult) => void
) {
    const parameters = { mcFluffy, typesWithObjects };
    dispatcher.send("addToMcFluffyServer", parameters, callback);
};

const setToMcFluffy = function (
    mcFluffy: TMcFluffyClass,
    objects: TMcFluffyObjectRef[],
    preservePreferDouble = false,
    callback: (res: ISetToMcFluffyResponse) => void
) {
    const parameters: TSetToMcFluffyParams = { mcFluffy, objects, preservePreferDouble };
    parameters.objects = objects.map((object) => ({
        id: object.object ? object.object.id : object.id,
        typeid: object.typeId ?? object.type?.id,
    }));
    if (preservePreferDouble === true) {
        parameters.preservePreferDouble = true;
    }
    dispatcher.send("setToMcFluffy", parameters, callback);
};

const setReservationToMcFluffy = function (
    reservationIds: number[],
    mcFluffy: TMcFluffyClass,
    modify: boolean,
    templateGroups: number[] | null,
    isNewReservation: boolean,
    callback: (result: ISetReservationToMcFluffyResponse) => void,
    errorCallback?: TApiErrorCallback
) {
    const parameters = { mcFluffy, reservationIds, modify, templateGroups, isNewReservation };
    dispatcher.send(
        "setReservationToMcFluffy",
        parameters,
        (result: ISetReservationToMcFluffyResponse | Error) => {
            // On HTTP 500/504 the dispatcher invokes the regular callback with an
            // Error. Returning only when an errorCallback was supplied let that
            // Error reach callback, which expects a response.
            if (result instanceof Error) {
                errorCallback?.("error", result);
                return;
            }
            callback(result);
        },
        false,
        errorCallback
    );
};

const setOrderRowToMcFluffy = function (
    orderRowId: number,
    mcFluffy: TMcFluffyClass,
    templateGroups: number[] | null,
    callback: (res: ISetOrderRowToMcFluffyResponse) => void
) {
    // In: orderRowId, fluffy, templateGroupIds
    const parameters = { orderRowId, mcFluffy, templateGroups };
    dispatcher.send("setOrderRowToMcFluffy", parameters, callback);
};

type TCancelReservationOpts = {
    longOperation?: boolean;
    singleTransaction?: boolean;
    addGroupReservations?: boolean;
};

const cancelReservation = function (
    reservationIds: number[],
    opts: TCancelReservationOpts = { longOperation: undefined },
    callback: (res: { parameters: TAPIStatusClass[] }) => void
) {
    const defaults = {
        singleTransaction: true,
        addGroupReservations: false,
    };
    const settings = _.extend({}, defaults, opts);

    let fullResult: TAPIStatusClass[] = [];
    const calls = _.splitArray(reservationIds, CANCEL_RESERVATIONS_BATCH_SIZE).map(
        (batch) => (done) => {
            dispatcher.send(
                "cancelReservations",
                { reservationIds: batch, ...settings },
                (result: ICancelReservationsResponse) => {
                    try {
                        fullResult = fullResult.concat(result.parameters[0]);
                    } catch (error) {
                        console.error("Error in cancelReservation", error);
                    } finally {
                        done();
                    }
                },
                opts.longOperation
            );
        }
    );
    _.runSync(calls, () => {
        callback({ parameters: fullResult });
    });
};

const restoreReservations = function (
    reservationIds: number[],
    allowDoubleReservation: boolean,
    callback: (res: IRestoreReservationsResponse) => void
) {
    dispatcher.send("restoreReservations", { reservationIds, allowDoubleReservation }, callback);
};

// Separate function just for clarity, invokes the same API call as restoreReservations above
const restoreReservationsFromHistory = function (
    reservationIds: number[],
    /** One modified timestamp per reservation, and the user behind each. */
    modified: number[],
    modifiedBy: number[],
    allowDoubleReservation: boolean,
    callback: (status: TAPIStatusClass[]) => void
) {
    dispatcher.send(
        "restoreReservations",
        {
            reservationIds,
            modified,
            modifiedBy,
            allowDoubleReservation,
        },
        (result: IRestoreReservationsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in restoreReservationsFromHistory", error);
            }
        }
    );
};

const removeFromMcFluffy = function (
    params: TRemoveFromMcFluffyParams,
    callback: (res: IRemoveFromMcFluffyResponse) => void
) {
    dispatcher.send("removeFromMcFluffy", params, callback);
};

const replaceInMcFluffy = function (
    params: TReplaceInMcFluffyParams,
    callback: (res: IReplaceInMcFluffyResponse) => void
) {
    dispatcher.send("replaceInMcFluffy", params, callback);
};

const reserveMcFluffy = function (
    mcFluffy: TMcFluffyClass,
    headerObjects: TObjectIdClass[],
    useAsyncSave: boolean,
    callback: (result: IReserveMcFluffyResponse) => void
) {
    dispatcher.send("reserveMcFluffy", { mcFluffy, headerObjects, useAsyncSave }, callback);
};

const getLimitation = function (
    functionName: string,
    propertyName: string,
    callback: (res: number) => void
) {
    if (limitCache[functionName]) {
        callback(limitCache[functionName][propertyName].value);
    } else {
        dispatcher.send("getLimitations", { functionName }, (result: IGetLimitationsResponse) => {
            try {
                const res = result.parameters;
                limitCache[functionName] = {};
                const numResults = res[0].length;
                let i;
                for (i = 0; i < numResults; i++) {
                    limitCache[functionName][res[0][i]] = {
                        value: res[1][i],
                        description: res[2][i],
                    };
                }
                if (limitCache[functionName][propertyName]) {
                    callback(limitCache[functionName][propertyName].value);
                } else {
                    callback(-1);
                }
            } catch (error) {
                console.error("getLimitations", error);
            }
        });
    }
};

const getUserLocale = function (
    languageString: string,
    useCurrentUser = true,
    callback: (result: IGetUserLocaleResponse) => void
) {
    dispatcher.send(
        "getUserLocale",
        { language: languageString, dateLanguage: languageString, useCurrentUser },
        callback
    );
};

const getCurrentUserPrefs = function (callback: (res: TNewUserArgs) => void) {
    dispatcher.send("getCurrentUserPrefs", {}, (result: IGetCurrentUserPrefsResponse) => {
        try {
            const parameters = result.parameters;
            const prefs = {
                isExternalUser: parameters[0],
                login: parameters[1],
                firstName: parameters[2],
                lastName: parameters[3],
                email: parameters[4],
                language: parameters[6],
                dateFormat: parameters[7],
                emailStatus: parameters[8].status,
                reservationStatus: parameters[13].status,
                timezone: parameters[14],
                displayTimezones: parameters[15],
                useCustomWeekNames: parameters[16],
            };
            callback(prefs as TNewUserArgs);
        } catch (error) {
            console.error("Error in getCurrentUserPrefs", error);
        }
    });
};

const setCurrentUserPrefs = function (
    /** The User model itself; the dispatcher serialises it as the payload. */
    userPrefs: object,
    callback: (ok: boolean) => void
) {
    dispatcher.send("setCurrentUserPrefs", userPrefs, (result: ISetCurrentUserPrefsResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in setCurrentUserPrefs", error);
        }
    });
};

const getObjectTemplate = function (
    typeIds: number[],
    callback: (res: IGetObjectTemplateResponse) => void
) {
    dispatcher.send("getObjectTemplate", { typeids: typeIds }, callback);
};

const getOrganizations = function (
    options: object,
    callback: (res: IGetOrganizationsResponse) => void
) {
    dispatcher.send("getOrganizations", options, callback);
};

const findHPeriods = function (callback: (periods: THPeriodNameClass[]) => void) {
    dispatcher.send("findHPeriods", {}, (result: IFindHPeriodsResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in findHPeriods", error);
        }
    });
};

const getHPeriodDefs = function (
    id: number,
    callback: (defs: IGetHPeriodDefsResponse["parameters"][0]) => void
) {
    dispatcher.send("getHPeriodDefs", { id }, (result: IGetHPeriodDefsResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getHPeriodDefs", error);
        }
    });
};

const prefsCache = {};

/**
 * Preferences are stored against a key, optionally scoped by references.
 */
const sendPreferences = function (
    key: string,
    refs: TSetPrefRef[] | undefined,
    values: TSetPrefValue[],
    isCurrentUser: boolean,
    callback?: (ok: boolean) => void
) {
    // Convert to string values.
    const stringValues = values.map((value) =>
        _.isBoolean(value) ? (value ? "true" : "false") : value
    );
    if (isCurrentUser) {
        const prefsKey = getPrefsKey(key, refs, undefined, true);
        if (_.isEqual(prefsCache[prefsKey], stringValues)) {
            callback?.(true);
            return;
        }
        prefsCache[prefsKey] = stringValues;
    } else {
        prefsCache[getPrefsKey(key, refs, undefined, false)] = undefined;
        prefsCache[getPrefsKey(key, refs, undefined, true)] = undefined;
    }
    dispatcher.send(
        "setPreferences",
        { key, references: refs, values: stringValues, language: undefined, isCurrentUser },
        (result: ISetPreferencesResponse) => {
            callback?.(result.parameters ? result.parameters.ok : false);
        }
    );
};

const setPreferences = function (
    key: string,
    values: TSetPrefValue[],
    callback?: (ok: boolean) => void
) {
    sendPreferences(key, undefined, values, true, callback);
};

const setPreferencesWithRefs = function (
    key: string,
    refs: TSetPrefRef[],
    values: TSetPrefValue[],
    callback?: (ok: boolean) => void
) {
    sendPreferences(key, refs, values, true, callback);
};

const setDefaultPreferences = function (
    key: string,
    refs: TSetPrefRef[],
    values: TSetPrefValue[],
    callback?: (ok: boolean) => void
) {
    sendPreferences(key, refs, values, false, callback);
};

const getPreferences = function (
    key: string,
    refs?: number[] | TRef[],
    language?: string,
    callback?: (res: string | boolean | string[] | null) => void
) {
    const isCurrentUser = language === undefined;
    const prefsKey = getPrefsKey(key, refs, language, isCurrentUser);
    if (prefsCache[prefsKey] !== undefined) {
        //console.error("Returning prefs value from cache", key);
        callback?.(parsePrefsValues(prefsCache[prefsKey], refs));
    } else {
        dispatcher.send(
            "getPreferences",
            { key, references: refs, language, isCurrentUser },
            (result: IGetPreferencesResponse) => {
                try {
                    const values = result.parameters.values;
                    prefsCache[prefsKey] = values;
                    callback?.(parsePrefsValues(values, refs));
                } catch (error) {
                    console.error("Error in getPreferences", error);
                }
            }
        );
    }
};

const getPreferencesMulti = function (keys: string[], callback) {
    dispatcher.send(
        "getPreferencesMulti",
        { keys, language: undefined },
        (result: IGetPreferencesMultiResponse) => {
            const parsedValues = {};
            try {
                Object.entries(result.parameters.values).forEach((entry) => {
                    parsedValues[entry[0]] = parsePrefValue(entry[1]);
                    prefsCache[entry[0]] = entry[1];
                });
            } catch (error) {
                console.error("Error in getPreferencesMulti", error);
            } finally {
                callback(parsedValues);
            }
        }
    );
};

const getOtherUserPrefs = function (callback: (res: IGetOtherUserPrefsResponse[0]) => void) {
    dispatcher.send("getOtherUserPrefs", {}, (result: IGetOtherUserPrefsResponse) => {
        try {
            callback(result[0]);
        } catch (error) {
            console.error("Error in getOtherUserPrefs", error);
        }
    });
};

const getStartupData = function (
    callback: (res: IGetStartupDataResponse["parameters"][0]) => void
) {
    dispatcher.send("getStartupData", {}, (result: IGetStartupDataResponse) => {
        try {
            callback(result[0]);
        } catch (error) {
            console.error("Error in getStartupData", error);
        }
    });
};

const getSupportInfo = function (
    callback: (
        supportURL: IGetSupportInfoResponse["parameters"][0],
        supportInfoLines: IGetSupportInfoResponse["parameters"][1],
        supportContacts: TSupportContact[]
    ) => void
) {
    dispatcher.send("getSupportInfo", {}, (result: IGetSupportInfoResponse) => {
        try {
            return callback(
                result.parameters[0],
                result.parameters[1],
                (result.parameters[2] as TSupportContact[]) || []
            );
        } catch (error) {
            console.error("Error in getSupportInfo", error);
            return;
        }
    });
};

const getEmailReservationPrefs = function (
    reservationIds: number[],
    callback: (emailSettings: TEmailReservationPrefs) => void
) {
    /*
            0: OK           boolean
            1: from         string
            2: toObjects    [string] - mottagare från objekt
            3: toMembers    [string] - mottagare från medlemmar
            4: ccMe         string - min adress om jag vill ha CC
            5: ccReserver   [string] - bokarens e-post om jag inte gjort bokningen
            6: fields       [fieldName] - bokningsfält som kan inkluderas
            7: subject      string
            8: message      string
            9: replyTo      string
        */
    dispatcher.send(
        "getEmailReservationPrefs",
        { reservationIds },
        (result: IGetEmailReservationPrefsResponse) => {
            try {
                const parsedResult: TEmailReservationPrefs = {
                    ok: result.parameters[0],
                    from: result.parameters[1],
                    toObjects: result.parameters[2],
                    toMembers: result.parameters[3],
                    ccMe: result.parameters[4],
                    ccReserver: result.parameters[5],
                    fields: result.parameters[6],
                    subject: result.parameters[7],
                    message: result.parameters[8],
                    replyTo: result.parameters[9],
                };
                return callback(parsedResult);
            } catch (error) {
                console.error("Error in getEmailReservationPrefs", error);
                return;
            }
        }
    );
};

const sendEmailReservation = function (
    emailSettings: TEmailReservationParams,
    callback: (error: { message: string; details: string } | null) => void
) {
    /*
            from            string
            to              [string]
            cc              [string]
            subject         [string]
            message         [string]
            reservationIds  [reservationId]
            fieldIds        [fieldId]
            replyTo        string
        */
    dispatcher.send(
        "sendEmailReservation",
        emailSettings,
        (result: ISendEmailReservationResponse) => {
            try {
                const errorObject = {
                    message: result.parameters[1],
                    details: result.parameters[2],
                };
                return callback(result.parameters[0] ? null : errorObject);
            } catch (error) {
                console.error("Error in sendEmailReservation", error);
                return null;
            }
        }
    );
};

const findOrderRowsList = function (
    query: TApiSearchQuery,
    callback: (result: IFindOrderRowsListResponse) => void,
    errorCallback: TApiErrorCallback = _.noop
) {
    dispatcher.send("findOrderRowsList", query, callback, true, errorCallback);
};

const getOrderRows = function (
    query: TApiSearchQuery,
    callback: (res: IGetOrderRowsResponse) => void
) {
    dispatcher.send("getOrderRows", query, callback);
};

const getAuthenticatedUserInfo = function (
    callback: (res: IGetAuthenticatedUserInfoResponse) => void
) {
    dispatcher.send("getAuthenticatedUserInfo", {}, callback);
};

const getModifiableObjectFields = function (
    objectId: number,
    callback: (res: IGetModifiableObjectFieldsResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "getModifiableObjectFields",
        { objectId },
        (result: IGetModifiableObjectFieldsResponse) => {
            try {
                return callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getModifiableObjectFields", error);
                return;
            }
        }
    );
};

const okToModifyObjects = function (
    objectIds: number | number[],
    callback: (res: IOkToModifyObjectsResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "okToModifyObjects",
        { objectIds: _.asArray(objectIds) },
        (result: IOkToModifyObjectsResponse) => {
            try {
                return callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in okToModifyObjects", error);
                return;
            }
        }
    );
};

const okToModifyOrders = function (
    orderIds: number | number[],
    callback: (permissions: IOkToModifyOrdersResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "okToModifyOrders",
        { orderIds: _.asArray(orderIds) },
        (result: IOkToModifyOrdersResponse) => {
            try {
                return callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in okToModifyOrders", error);
            }
        }
    );
};

const moveReservationsInTime = function (
    reservationIds: number[],
    numSeconds: number,
    addGroupReservations: boolean,
    doubleReserveAll: boolean,
    durationSeconds: number,
    isDurationRelative: boolean,
    callback: (result: IMoveReservationsInTimeResponse) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "moveReservationsInTime",
        {
            timeout: 0,
            reservationIds,
            seconds: numSeconds,
            addGroupReservations,
            doubleReserveAll,
            durationSeconds,
            isDurationRelative,
        },
        (result) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in moveReservationsInTime", error);
            }
        },
        true,
        errorCallback
    );
};

const copyReservationsInTime = function (
    reservationIds: number[],
    numSeconds: number,
    addGroupReservations: boolean,
    doubleReserveAll: boolean,
    callback: (result: ICopyReservationsInTimeResponse["parameters"]) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "copyReservationsInTime",
        { timeout: 0, reservationIds, seconds: numSeconds, addGroupReservations, doubleReserveAll },
        (result: ICopyReservationsInTimeResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in copyReservationsInTime", error);
            }
        },
        true,
        errorCallback
    );
};

const replaceObjectsOnReservations = function (
    reservationIds: number[],
    /** One id, several, or null when only the type is being replaced. */
    newObjectId: number | number[] | null,
    typeId: number,
    oldObjectId: number | null,
    oldTypeId: number | null | undefined,
    allowAvailabilityOverlap: boolean,
    addIfMissingTypeOnly: boolean,
    allowIncomplete: boolean,
    allowDoubleBooking: boolean,
    callback: (result: IReplaceObjectsOnReservationsResponse) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "replaceObjectsOnReservations",
        {
            timeout: 0,
            reservationIds,
            newObjectId,
            typeId,
            oldObjectId,
            oldTypeId,
            allowAvailabilityOverlap,
            addIfMissingTypeOnly,
            allowIncomplete,
            allowDoubleBooking,
        },
        (result) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in replaceObjectsOnReservations", error);
            }
        },
        true,
        errorCallback
    );
};

const dayNumberFromString = function (
    dateString: string,
    callback: (result: IDayNumberFromStringResponse["parameters"]) => void
) {
    dispatcher.send("dayNumberFromString", { dateString }, (result: IDayNumberFromStringResponse) =>
        callback(result.parameters)
    );
};

const reservationClusterChangeTime = function (
    /** One list of ids per cluster, so this nests. */
    reservationIds: number[][],
    groupIds: number[],
    /** One list of slots per cluster, matching reservationIds. */
    timeslots: TTimeSlotClass[][],
    allowAsymmetric: boolean,
    copy: boolean,
    allowDoubleObjects: number[],
    allowAvailabilityOverlap: boolean,
    newObjects: TObjectIdClass[],
    oldObjects: TObjectIdClass[],
    /** One entry per cluster; null where the cluster has no capacity source. */
    capacityReservationIds: (number | null)[],
    useAsyncSave: boolean,
    callback: (result: IReservationClusterChangeTimeResponse) => void
) {
    dispatcher.send(
        "reservationClusterChangeTime",
        {
            reservationIds,
            groupIds,
            timeslots,
            allowAsymmetric,
            copy,
            allowDoubleObjects,
            allowAvailabilityOverlap,
            newObjects,
            oldObjects,
            capacityReservationIds,
            useAsyncSave,
        },
        (result) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in reservationClusterChangeTime", error);
            }
        }
    );
};

const getValidReservationStatus = function (
    statusList: number[],
    callback: (statuses: TReservationStatusClass[]) => void
) {
    dispatcher.send(
        "getValidReservationStatus",
        { statusList },
        (result: IGetValidReservationStatusResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getValidReservationStatus", error);
            }
        }
    );
};

const getReservationStatus = function (
    reservationIds: number[],
    callback: (result: IGetReservationStatusResponse["parameters"]) => void
) {
    dispatcher.send(
        "getReservationStatus",
        { reservationIds },
        (result: IGetReservationStatusResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in getReservationStatus", error);
            }
        }
    );
};

const setReservationStatus = function (
    reservationIds: number[],
    status: number,
    callback: (result: ISetReservationStatusResponse["parameters"]) => void
) {
    dispatcher.send(
        "setReservationStatus",
        { reservationIds, status },
        (result: ISetReservationStatusResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in setReservationStatus", error);
            }
        },
        true
    );
};

const createReservationLayer = function (
    name: string,
    description: string,
    callback: (layer: TReservationLayerNameClass, status: TAPIStatusClass) => void
) {
    dispatcher.send(
        "createReservationLayer",
        { name, description },
        (result: ICreateReservationLayerResponse) => {
            try {
                callback(result.parameters[0], result.parameters[1]);
            } catch (error) {
                console.error("Error in createReservationLayer", error);
            }
        }
    );
};

// If deleteReservations is false, the layer won't be deleted if there are any reservations on it
const deleteReservationLayer = function (
    layerId: number,
    deleteReservations: boolean,
    callback: (deleted: boolean) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "deleteReservationLayer",
        { layerId, deleteReservations },
        (result: IDeleteReservationLayerResponse) => {
            try {
                // deleted - true or false
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in deleteReservationLayer", error);
            }
        },
        true,
        errorCallback
    );
};

const addToReservationLayer = function (
    layerId: number,
    reservationIds: number[],
    move: boolean,
    otherReservationIds: number[],
    addGroupReservations: boolean,
    ignoreFields: boolean,
    groupMap: Record<string, unknown> | null,
    callback: (result: TAddToReservationLayerResult) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "addToReservationLayer",
        {
            layerId,
            reservationIds,
            move,
            otherReservationIds,
            addGroupReservations,
            ignoreFields,
            groupMap,
        },
        (result: IAddToReservationLayerResponse) => {
            try {
                // Status array, all reservations, bool success
                callback({
                    status: result.parameters[0],
                    allReservations: result.parameters[1],
                    success: result.parameters[2],
                    groupMap: result.parameters[3],
                });
            } catch (error) {
                console.error("Error in addToReservationLayer", error);
            }
        },
        true,
        errorCallback
    );
};

const deleteFromReservationLayer = function (
    reservationIds: number[],
    callback: (statuses: TAPIStatusClass[]) => void,
    errorCallback?: TApiErrorCallback
) {
    dispatcher.send(
        "deleteFromReservationLayer",
        { reservationIds },
        (result: IDeleteFromReservationLayerResponse) => {
            try {
                // Status array
                callback(result.parameters[0]);
            } catch (error) {
                console.error("deleteFromReservationLayer", error);
            }
        },
        true,
        errorCallback
    );
};

const persistReservationLayer = function (
    /** An array persists those reservations; a number persists a whole layer. */
    layerIdOrReservationIds: number | number[],
    ignoreUnchangedReservations: boolean,
    allowDoubleReservations: boolean,
    callback: (result: IPersistReservationLayerResponse["parameters"][0]) => void,
    errorCallback?: TApiErrorCallback
) {
    if (_.isArray(layerIdOrReservationIds)) {
        dispatcher.send(
            "persistReservationLayer",
            {
                reservationIds: layerIdOrReservationIds,
                ignoreUnchangedReservations,
                allowDoubleReservations,
            },
            (result: IPersistReservationLayerResponse) => {
                try {
                    callback(result.parameters[0]);
                } catch (error) {
                    console.error("Error in persistReservationLayer", error);
                }
            },
            true,
            errorCallback
        );
        return;
    }
    dispatcher.send(
        "persistReservationLayer",
        { layerId: layerIdOrReservationIds, ignoreUnchangedReservations, allowDoubleReservations },
        (result) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in persistReservationLayer", error);
            }
        },
        true,
        errorCallback
    );
};

const getReservationLayers = function (
    layerKind: number,
    callback: (layers: TReservationLayerNameClass[]) => void
) {
    dispatcher.send(
        "getReservationLayers",
        { layerKind },
        (result: IGetReservationLayersResponse) => {
            try {
                // Kind 3 is experiment layers
                let layers = result.parameters[0];
                layers.forEach((layer) => {
                    if (!layer.name) {
                        layer.name = String(layer.id);
                    }
                });
                callback(layers);
            } catch (error) {
                console.error("Error in getReservationLayers", error);
            }
        }
    );
};

const getReservationsOnLayer = function (
    layerId: number,
    callback: (result: IGetReservationsOnLayerResponse["parameters"]) => void
) {
    dispatcher.send(
        "getReservationsOnLayer",
        { layerId },
        (result: IGetReservationsOnLayerResponse) => {
            // 0 - new, 1 - modified, 2 - cancelled
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in getReservationsOnLayer", error);
            }
        }
    );
};

const renameReservationLayer = function (
    layerId: number,
    name: string,
    description: string | undefined,
    callback: (status: TAPIStatusClass) => void
) {
    dispatcher.send(
        "renameReservationLayer",
        { layerId, name, description },
        (result: IRenameReservationLayerResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in renameReservationLayer", error);
            }
        }
    );
};

const hasExternalPermissions = function (
    permissions: string[],
    callback: (res: IHasExternalPermissionsResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "hasExternalPermissions",
        { permissions },
        (result: IHasExternalPermissionsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in hasExternalPermissions", error);
            }
        }
    );
};

// Checks the given layer for collisions with the base layer.
// Can be extended - by adding the collisionLayer parameter - to check against other layers
const getReservationCollisions = function (
    layerId: number | undefined,
    callback: (collisions: TReservationCollision[]) => void
) {
    dispatcher.send(
        "getReservationCollisions",
        { layerId, collisionLayerId: 0 },
        (result: IGetReservationCollisionsResponse) => {
            try {
                // [0] - reservations, [1] their colliding reservations, [2] their colliding objects, [3] their colliding info reservations
                const cR = result.parameters[1];
                const cO = result.parameters[2];
                const cI = result.parameters[3];
                callback(
                    result.parameters[0].map((res, index) => ({
                        id: res.id,
                        collidingReservations: cR[index].map((r) => r.id),
                        collidingObjects: cO[index].map((o) => o.id),
                        collidingInfoReservations: cI[index].map((i) => i.id),
                    }))
                );
            } catch (error) {
                console.error("Error in getReservationCollisions", error);
            }
        },
        true
    );
};

const groupReservations = function (
    reservationIds: number[],
    clusterKind: TClusterKindValue = ClusterKind.DATE,
    callback: (res: IGroupReservationsResponse["parameters"]) => void = _.noop
) {
    dispatcher.send(
        "groupReservations",
        { clusterKind, reservationIds },
        (result: IGroupReservationsResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in groupReservations", error);
            }
        }
    );
};

const getGroupedEntries = function (
    clusterKind: TClusterKindValue = ClusterKind.NONE,
    reservationIds: number[],
    opts: { typeFields?: number[] | null } = { typeFields: null },
    callback: (res: IGetGroupedEntriesResponse["parameters"][0]) => void = _.noop
) {
    dispatcher.send(
        "getGroupedEntries",
        { clusterKind, reservationIds, typeFields: opts.typeFields || [] },
        (result: IGetGroupedEntriesResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getGroupedEntries", error);
            }
        }
    );
};

const findSumSettings = function (
    callback: (result: IFindSumSettingsResponse["parameters"]) => void
) {
    dispatcher.send("findSumSettings", {}, (result: IFindSumSettingsResponse) => {
        try {
            callback(result.parameters);
        } catch (error) {
            console.error("Error in findSumSettings", error);
        }
    });
};

const getSumSetting = function (
    ids: number[] = [],
    callback: (settings: IGetSumSettingResponse["parameters"][0]) => void
) {
    dispatcher.send("getSumSetting", { ids }, (result: IGetSumSettingResponse) => {
        try {
            result.parameters[0].forEach((sumSetting) => {
                sumSetting.types = sumSetting.types.map((type) => type.id);
                sumSetting.includeMembers = _.filter(
                    sumSetting.types,
                    (type, index) => sumSetting.includeMembers[index] === true
                );
            });
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getSumSetting", error);
        }
    });
};

const setSumSetting = function (
    sumSetting: object,
    callback: (result: { ok: boolean; id: number }) => void
) {
    dispatcher.send("setSumSetting", sumSetting, (result: ISetSumSettingResponse) => {
        try {
            callback({ ok: result.parameters[0], id: result.parameters[1] });
        } catch (error) {
            console.error("Error in setSumSetting", error);
        }
    });
};

const deleteSumSetting = function (id: number, callback: (deleted: boolean) => void) {
    dispatcher.send("deleteSumSetting", { id }, (result: IDeleteSumSettingResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in deleteSumSetting", error);
        }
    });
};

const getColorTypes = function (callback: (res: IGetColorTypesResponse["parameters"][0]) => void) {
    dispatcher.send("getColorTypes", {}, (result: IGetColorTypesResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getColorTypes", error);
        }
    });
};

const getTypesOnReservations = function (
    reservationIds: number[],
    callback: (types: TTypeNameClass[]) => void
) {
    dispatcher.send(
        "getTypesOnReservations",
        { reservationIds },
        (result: IGetTypesOnReservationsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getTypesOnReservations", error);
            }
        }
    );
};

const getObjectsOnReservations = function (
    reservationIds: number[],
    typeId: number,
    callback: (objects: TObjectIdClass[], occurrences: number[]) => void
) {
    dispatcher.send(
        "getObjectsOnReservations",
        { reservationIds, typeId, reservable: false },
        (result: IGetObjectsOnReservationsResponse) => {
            try {
                //[0]: Objects, [1]: occurances on reservations
                callback(result.parameters[0], result.parameters[1]);
            } catch (error) {
                console.error("Error in getObjectsOnReservations", error);
            }
        }
    );
};

const getReservableTypesForType = function (
    typeId: number | null,
    callback: (types: TTypeNameClass[]) => void
) {
    dispatcher.send(
        "getReservableTypesForType",
        typeId !== null ? { typeId } : {},
        (result: IGetReservableTypesForTypeResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getReservableTypesForType", error);
            }
        }
    );
};

const getReservationsWithObjects = function (
    reservationIds: number[],
    removeObjectIds: number[],
    addObjectIds: number[],
    addTypeId: number,
    addIfMissingTypeOnly: boolean,
    callback: (reservations: TReservationIdClass[]) => void
) {
    dispatcher.send(
        "getReservationsWithObjects",
        {
            reservationIds,
            removeObjectIds,
            addObjectIds,
            addTypeId,
            addIfMissingTypeOnly,
        },
        (result: IGetReservationsWithObjectsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getReservationsWithObjects", error);
            }
        }
    );
};

const getReservationsWithTime = function (
    reservationIds: number[],
    addGroupReservations: boolean,
    /** The wrapper spreads the response, so this takes five positional arguments. */
    callback: (
        success: boolean,
        reservationIds: TReservationIdClass[],
        timeslots: TTimeSlotClass[],
        firstBegin: number,
        lastEnd: number
    ) => void,
    errorCallback: TApiErrorCallback = _.noop
) {
    dispatcher.send(
        "getReservationsWithTime",
        { reservationIds, addGroupReservations },
        (result: IGetReservationsWithTimeResponse) => {
            try {
                // Success, reservationIds, timeslots, firstBegin, lastEnd
                callback(
                    result.parameters[0],
                    result.parameters[1],
                    result.parameters[2],
                    result.parameters[3],
                    result.parameters[4]
                );
            } catch (error) {
                console.error("Error in getReservationsWithTime", error);
            }
        },
        true,
        errorCallback
    );
};

const findPeriods = function (
    callback: (periods: TPeriodNameClass[], totalNumber: number) => void
) {
    dispatcher.send("findPeriods", {}, (result: IFindPeriodsResponse) => {
        try {
            callback(result.parameters[0], result.parameters[1]); // Periods, total number of rows
        } catch (error) {
            console.error("Error in findPeriods", error);
        }
    });
};

const okToSetReservationOrganizations = function (
    reservationIds: number[],
    callback: (isOk: boolean) => void
) {
    dispatcher.send(
        "okToSetReservationOrganizations",
        { reservationIds },
        (result: IOkToSetReservationOrganizationsResponse) => {
            try {
                callback(result.parameters[0]); // Just returns true or false
            } catch (error) {
                console.error("Error in okToSetReservationOrganizations", error);
            }
        }
    );
};

const setReservationOrganizations = function (
    reservationIds: number[],
    organizationIds: number[],
    callback: (status: TAPIStatusClass[]) => void
) {
    dispatcher.send(
        "setReservationOrganizations",
        { reservationIds, organizationIds },
        (result: ISetReservationOrganizationsResponse) => {
            try {
                callback(result.parameters[0]); // Array of status
            } catch (error) {
                console.error("Error in setReservationOrganizations", error);
            }
        }
    );
};

const getTimezones = function (
    callback: (
        res: {
            shortName: string;
            name: string;
            offset: string;
        }[]
    ) => void
) {
    dispatcher.send("getTimeZones", {}, (result: IGetTimeZonesResponse) => {
        try {
            const zones = result.parameters[0].map((shortName, index) => ({
                shortName,
                name: result.parameters[2][index],
                offset: result.parameters[1][index],
            }));
            callback(zones);
        } catch (error) {
            console.error("Error in getTimezones", error);
        }
    });
};

const getCurrentDateTime = function (callback: (res: MillenniumDateTime) => void) {
    dispatcher.send("getCurrentDateTime", {}, (result: IGetCurrentDateTimeResponse) => {
        try {
            callback(new MillenniumDateTime(result.parameters[0].datetime));
        } catch (error) {
            console.error("Error in getCurrentDateTime", error);
        }
    });
};

const getMemberTypesForReservations = function (reservationIds: number[], callback) {
    dispatcher.send(
        "getMemberTypesForReservations",
        { reservationIds },
        (result: IGetMemberTypesForReservationsResponse) => {
            try {
                callback({
                    parentMemberTypes: result.parameters[0],
                    childMemberTypes: result.parameters[1],
                });
            } catch (error) {
                console.error("Error in getMemberTypesForReservations", error);
            }
        }
    );
};

const getMemberObjectsForReservations = function (
    reservationIds: number[],
    groupTypeId: number,
    memberTypeId: number,
    callback: (objects: TObjectIdClass[]) => void
) {
    dispatcher.send(
        "getMemberObjectsForReservations",
        {
            reservationIds,
            groupTypeId,
            memberTypeId,
        },
        (result: IGetMemberObjectsForReservationsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getMemberObjectsForReservations", error);
            }
        }
    );
};

const getMemberExceptionObjectsForReservations = function (
    reservationIds: number[],
    typeId: number,
    callback: (objects: TObjectIdClass[]) => void
) {
    dispatcher.send(
        "getMemberExceptionObjectsForReservations",
        { reservationIds, typeId },
        (result: IGetMemberExceptionObjectsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in getMemberExceptionObjectsForReservations", error);
            }
        }
    );
};

const saveExceptionObjectsForReservations = function (
    reservationIds: number[],
    addObjectTypes: number[],
    addObjects: number[],
    removeObjects: number[],
    callback: (result: ISaveExceptionObjectsResponse["parameters"]) => void
) {
    dispatcher.send(
        "saveExceptionObjectsForReservations",
        { reservationIds, addObjectTypes, addObjects, removeObjects },
        (result: ISaveExceptionObjectsResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in saveExceptionObjectsForReservations", error);
            }
        }
    );
};

const canHaveMemberExceptionObjectsForReservations = function (
    reservationIds: number[],
    callback: (canHave: boolean) => void
) {
    dispatcher.send(
        "canHaveMemberExceptionObjectsForReservations",
        { reservationIds },
        (result: ICanHaveMemberExceptionObjectsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in canHaveMemberExceptionObjectsForReservations", error);
            }
        }
    );
};

const findObjectFieldsForReservationList = function (
    /** null asks for every kind. */
    fieldKinds: number[] | null,
    callback: (res: IFindObjectFieldsForReservationListResponse["parameters"][0]) => void
) {
    dispatcher.send(
        "findObjectFieldsForReservationList",
        { fieldKinds },
        (result: IFindObjectFieldsForReservationListResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in findObjectFieldsForReservationList", error);
            }
        }
    );
};

/** The category and checkbox fields a reservation list can filter on. */
type TObjectCategories = {
    availableReservationCategories: TFieldDefClass[];
    availableReservationCheckboxes: TFieldDefClass[];
    availableObjectCategories: TFieldDefClass[];
    availableObjectCheckboxes: TFieldDefClass[];
};

const getObjectCategories = (callback: (res: TObjectCategories) => void) => {
    getFieldDefsForReservations((reservationFields) => {
        findObjectFieldsForReservationList(null, (objectFields) => {
            getFieldDefs(
                objectFields.map((fld) => fld.id),
                undefined,
                (defs) => {
                    const reservationCategories = reservationFields.filter(
                        (field) => field.kind === FieldKind.CATEGORY
                    );
                    const reservationCheckboxes = reservationFields.filter(
                        (field) => field.kind === FieldKind.BOOLEAN
                    );
                    const categories = defs.filter((field) => field.kind === FieldKind.CATEGORY);
                    const checkboxes = defs.filter((field) => field.kind === FieldKind.BOOLEAN);
                    callback({
                        availableReservationCategories: reservationCategories,
                        availableReservationCheckboxes: reservationCheckboxes,
                        availableObjectCategories: categories,
                        availableObjectCheckboxes: checkboxes,
                    });
                }
            );
        });
    });
};

const moveReservationsFromWaitingList = (
    reservationId: number,
    timeslots: TTimeSlotClass[],
    allowDoubleObjects: number[],
    allowAvailabilityOverlap: boolean,
    capacityReservationIds: (number | null)[] | undefined,
    useAsyncSave: boolean,
    callback: (result: IMoveReservationsFromWaitingListResponse["parameters"]) => void
) => {
    dispatcher.send(
        "moveReservationsFromWaitingList",
        {
            reservationId,
            timeslots,
            allowDoubleObjects,
            allowAvailabilityOverlap,
            capacityReservationIds,
            useAsyncSave,
        },
        (result: IMoveReservationsFromWaitingListResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in moveReservationsFromWaitingList", error);
            }
        }
    );
};

const moveReservationsToWaitingList = (
    reservationIds: number[],
    createSingleReservation: boolean,
    useAsyncSave: boolean,
    callback: (result: IMoveReservationsToWaitingListResponse["parameters"]) => void
) => {
    dispatcher.send(
        "moveReservationsToWaitingList",
        { reservationIds, createSingleReservation, useAsyncSave },
        (result: IMoveReservationsToWaitingListResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in moveReservationsToWaitingList", error);
            }
        }
    );
};

const showEnableCustomWeekNames = (
    callback: (res: IShowEnableCustomWeekNamesResponse["parameters"][0]) => void
) => {
    dispatcher.send(
        "showEnableCustomWeekNames",
        {},
        (result: IShowEnableCustomWeekNamesResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in showEnableCustomWeekNames", error);
            }
        }
    );
};

const getCustomWeekNames = (
    beginDayNumber: number,
    endDayNumber: number,
    callback: (names: ReturnType<typeof NamedWeek.create>) => void
) => {
    dispatcher.send(
        "getCustomWeekNames",
        { beginDayNumber, endDayNumber },
        (result: IGetCustomWeekNamesResponse) => {
            try {
                callback(NamedWeek.create(result.parameters[0]));
            } catch (error) {
                console.error("Error in getCustomWeekNames", error);
            }
        }
    );
};

const targetExternalAppsEnvironment = (
    callback: (res: ITargetExternalAppsEnvironmentResponse["parameters"][0]) => void
) => {
    dispatcher.send(
        "targetExternalAppsEnvironment",
        {},
        (result: ITargetExternalAppsEnvironmentResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in targetExternalAppsEnvironment", error);
            }
        }
    );
};

const getUserSearchFields = (typeId: number, callback: (fields: number[] | null) => void) => {
    if (userSearchFieldCache !== null) {
        return callback(userSearchFieldCache[typeId] || null);
    }
    getPreferences("userSearchFields", undefined, undefined, (result) => {
        if (result) {
            userSearchFieldCache = JSON.parse(result as string);
            callback(userSearchFieldCache?.[typeId] || null);
        } else {
            callback(null);
        }
    });
};

const setUserSearchFields = (flds: number[], typeId: number) => {
    if (userSearchFieldCache === null) {
        userSearchFieldCache = {};
    }
    userSearchFieldCache[typeId] = flds;
    setPreferences("userSearchFields", [JSON.stringify(userSearchFieldCache)], _.noop);
};

const addTagToReservations = (
    reservationIds: number[],
    tag: string,
    callback: (result: IAddTagToReservationsResponse["parameters"]) => void
) => {
    dispatcher.send(
        "addTagToReservations",
        { reservationIds, tag },
        (result: IAddTagToReservationsResponse) => {
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in addTagToReservations", error);
            }
        }
    );
};

const removeTagFromReservations = (
    reservationIds: number[],
    tag: string,
    callback: (status: TAPIStatusClass) => void
) => {
    dispatcher.send(
        "removeTagFromReservations",
        { reservationIds, tag },
        (result: IRemoveTagFromReservationsResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in removeTagFromReservations", error);
            }
        }
    );
};

const createTag = (tag: string, callback: (status: TAPIStatusClass) => void) => {
    dispatcher.send("createTag", { tag }, (result: ICreateTagResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in createTag", error);
        }
    });
};

const findTags = (
    searchText: string,
    callback: (result: { count: number; tags: string[]; isCurrentUserTag: boolean[] }) => void
) => {
    dispatcher.send("findTags", { searchText }, (result: IFindTagsResponse) => {
        try {
            callback({
                count: result.parameters[0],
                tags: result.parameters[1],
                isCurrentUserTag: result.parameters[2],
            });
        } catch (error) {
            console.error("Error in findTags", error);
        }
    });
};

const findMyTags = (
    searchText: string,
    callback: (result: { count: number; tags: string[]; isCurrentUserTag: boolean[] }) => void
) => {
    dispatcher.send("findTags", { searchText, myTags: true }, (result) => {
        try {
            callback({
                count: result.parameters[0],
                tags: result.parameters[1],
                isCurrentUserTag: result.parameters[2],
            });
        } catch (error) {
            console.error("Error in findMyTags", error);
        }
    });
};

const okToDeleteReservationsCancelled = (
    reservationIds: number[],
    /**
     * An empty list asks whether deleting is allowed at all and answers one
     * boolean; a non-empty one answers a boolean per reservation.
     */
    callback: (result: boolean | boolean[]) => void
) => {
    if (reservationIds.length === 0) {
        dispatcher.send(
            "okToDeleteReservationsCancelled",
            { reservationIds },
            (result: IOkToDeleteReservationsCancelledResponse) => {
                try {
                    callback(result.parameters[0]);
                } catch (error) {
                    console.error("Error in okToDeleteReservationsCancelled", error);
                }
            }
        );
    } else {
        let fullResult: boolean[] = [];
        const calls = _.splitArray(reservationIds, STATUS_BATCH_SIZE).map((batch) => (done) => {
            dispatcher.send(
                "okToDeleteReservationsCancelled",
                { reservationIds: batch },
                (result: IOkToDeleteReservationsCancelledResponse) => {
                    try {
                        fullResult = fullResult.concat(result.parameters[0]);
                    } catch (error) {
                        console.error("Error in okToDeleteReservationsCancelled", error);
                    } finally {
                        done();
                    }
                },
                true
            );
        });
        _.runSync(calls, () => {
            callback(fullResult);
        });
    }
};

const deleteReservationsCancelled = (
    reservationIds: number[],
    callback: (results: boolean[]) => void
) => {
    let fullResult: boolean[] = [];
    const calls = _.splitArray(reservationIds, STATUS_BATCH_SIZE).map((batch) => (done) => {
        dispatcher.send(
            "deleteReservationsCancelled",
            { reservationIds: batch },
            (result: IDeleteReservationsCancelledResponse) => {
                try {
                    fullResult = fullResult.concat(result.parameters[0]);
                } catch (error) {
                    console.error("Error in deleteReservationsCancelled", error);
                } finally {
                    done();
                }
            },
            true
        );
    });
    _.runSync(calls, () => {
        callback(fullResult);
    });
};

const getReservationConflicts = (
    query: TApiSearchQuery,
    callback: (res: IGetReservationConflictsResponse["parameters"]) => void
) => {
    // typeid
    // objectIds
    // start
    // end
    // includeUnintentionalDoubleReservations = true
    // includeIntentionalDoubleReservations = false
    // filterObjectIds
    // filterReservationModes
    // includeCollisionsOutsideOfFilters = false
    dispatcher.send(
        "getReservationConflicts",
        query,
        (result: IGetReservationConflictsResponse) => {
            // Returns five arrays
            // doubleReservationObjects
            // firstReservations
            // firstObjects
            // secondReservations
            // secondObjects
            try {
                callback(result.parameters);
            } catch (error) {
                console.error("Error in getReservationConflicts", error);
            }
        },
        true
    );
};

const getOrgInfo = function (
    environment: string,
    orgId: string,
    region: string,
    callback: (info: IGetOrgInfoResponse["parameters"][0] | null) => void
) {
    dispatcher.send(
        "getOrgInfo",
        {
            environment,
            orgId,
            region,
            te_auth_token: window.localStorage.te_auth_token
                ? JSON.parse(window.localStorage.te_auth_token).accessToken
                : undefined,
        },
        (response: IGetOrgInfoResponse | IGetOrgInfoResponse["parameters"][0]) => {
            try {
                callback("parameters" in response ? response.parameters[0] : response);
            } catch (error) {
                console.error("Error in getOrgInfo", error);
                callback(null);
            }
        },
        false,
        () => callback(null)
    );
};

const getExamSettings = function (
    environment: string,
    callback: (res: IGetExamSettingsResponse["parameters"][0] | false) => void,
    errorCallback
) {
    const platform = "3";
    dispatcher.send(
        "getExamSettings",
        {
            environment,
            platform,
            te_auth_token: window.localStorage.te_auth_token
                ? JSON.parse(window.localStorage.te_auth_token).accessToken
                : undefined,
        },
        (response: IGetExamSettingsResponse | IGetExamSettingsResponse["parameters"][0]) => {
            try {
                return callback("parameters" in response ? response.parameters[0] : response);
            } catch (error) {
                console.error("Error in getExamSettings", error);
                return;
            }
        },
        false,
        errorCallback
    );
};

const getServerEnv = (callback: (res: IGetServerEnvResponse["parameters"][0]) => void) => {
    dispatcher.send("getServerEnv", {}, (result: IGetServerEnvResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getServerEnv", error);
        }
    });
};

const loginWithSSOToken = (
    token: string | null,
    callback: (res: ILoginWithSSOTokenResponse) => void
) => {
    dispatcher.send("loginWithSSOToken", { token }, (result: ILoginWithSSOTokenResponse) => {
        try {
            callback(result[0]);
        } catch (error) {
            console.error("Error in loginWithSSOToken", error);
        }
    });
};

const logoutUser = (callback: (res: ILogoutUserResponse) => void) => {
    dispatcher.send("logoutUser", {}, (result: ILogoutUserResponse) => {
        try {
            callback(result);
        } catch (error) {
            console.error("Error in logoutUser", error);
        }
    });
};

const addReservationsToReservationGroup = function (
    groupId: number,
    reservationIds: number[],
    callback: (status: TAPIStatusClass) => void
) {
    dispatcher.send(
        "addReservationsToReservationGroup",
        { groupId, reservationIds },
        (result: IAddReservationsToReservationGroupResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in addReservationsToReservationGroup", error);
            }
        }
    );
};

const removeReservationsFromReservationGroup = function (
    groupId: number,
    reservationIds: number[],
    callback: (status: TAPIStatusClass) => void
) {
    dispatcher.send(
        "removeReservationsFromReservationGroup",
        { groupId, reservationIds },
        (result: IRemoveReservationsFromGroupResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in removeReservationsFromReservationGroup", error);
            }
        }
    );
};

const createReservationGroup = function (
    name: string,
    extid: string | null,
    callback: (res: ICreateReservationGroupResponse) => void = _.noop
) {
    dispatcher.send(
        "createReservationGroup",
        { extid, name },
        (result: ICreateReservationGroupResponse) => {
            callback(result);
        }
    );
};

const updateReservationGroup = function (
    id: number,
    extid: string,
    name: string,
    callback: (status: TAPIStatusClass) => void = _.noop
) {
    dispatcher.send(
        "updateReservationGroup",
        { id, extid, name },
        (result: IUpdateReservationGroupResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in updateReservationGroup", error);
            }
        }
    );
};

const deleteReservationGroup = function (
    groupIds: number | number[],
    callback: (res: IDeleteReservationGroupResponse["parameters"][0]) => void = _.noop
) {
    let groupId = _.isArray(groupIds) ? groupIds[0] : groupIds;
    dispatcher.send(
        "deleteReservationGroup",
        { groupId },
        (result: IDeleteReservationGroupResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in deleteReservationGroup", error);
            }
        }
    );
};

const getReservationGroup = function (
    groupId: number,
    callback: (res: { id: number; extId: string; name: string } | false) => void = _.noop
) {
    dispatcher.send("getReservationGroup", { groupId }, (result: IGetReservationGroupResponse) => {
        try {
            if (result.parameters[0] === false) {
                callback(false);
                return;
            }
            callback({
                id: result.parameters[1],
                extId: result.parameters[2],
                name: result.parameters[3],
            });
        } catch (error) {
            console.error("Error in getReservationGroup", error);
            callback(false);
        }
    });
};

const createReservationGroupWithReservations = function (
    name: string,
    reservationIds: number[],
    callback: (groupdId: number) => void = _.noop,
    errorCallback: (details: string | undefined) => void = _.noop
) {
    createReservationGroup(name, null, (result) => {
        try {
            if (result.parameters[1]) {
                if (!reservationIds || reservationIds.length === 0) {
                    callback(result.parameters[1]);
                } else {
                    addReservationsToReservationGroup(
                        result.parameters[1],
                        reservationIds,
                        (addResult) => {
                            if (addResult.details) {
                                deleteReservationGroup(result.parameters[1], _.noop);
                                errorCallback(addResult.details);
                            } else {
                                callback(result.parameters[1]);
                            }
                        }
                    );
                }
            } else {
                errorCallback(result.parameters[0].details);
            }
        } catch (error) {
            console.error("Error in createReservationGroupWithReservations", error);
        }
    });
};

const getPaddingRules = function (
    ruleIds: number[],
    callback: (res: IGetPaddingRuleNamesResponse["parameters"][0]) => void = _.noop
) {
    dispatcher.send("getPaddingRuleNames", { ruleIds }, (result: IGetPaddingRuleNamesResponse) => {
        try {
            callback(result.parameters[0]);
        } catch (error) {
            console.error("Error in getPaddingRules", error);
        }
    });
};

const lockReservations = function (
    reservationIds: number[],
    reason: string,
    callback: (res: IAddReservationsLockResponse["parameters"][0]) => void = _.noop
) {
    dispatcher.send(
        "addReservationsLock",
        { reservationIds, reason },
        (result: IAddReservationsLockResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in lockReservations", error);
            }
        }
    );
};

const unlockReservations = function (
    reservationIds: number[],
    callback: (res: IRemoveReservationsLockResponse["parameters"][0]) => void = _.noop
) {
    dispatcher.send(
        "removeReservationsLock",
        { reservationIds },
        (result: IRemoveReservationsLockResponse) => {
            try {
                callback(result.parameters[0]);
            } catch (error) {
                console.error("Error in unlockReservations", error);
            }
        }
    );
};

const setReservationCapacity = function (
    reservationIds: number[],
    allowUnderCapacity: boolean,
    capacity: number,
    ordering: number,
    callback: (res: ISetReservationCapacityResponse["parameters"][0]) => void = _.noop
) {
    dispatcher.send(
        "setReservationCapacity",
        { reservationIds, allowUnderCapacity, capacity, ordering },
        (result: ISetReservationCapacityResponse) => {
            callback(result.parameters[0]);
        }
    );
};

const partitionObject = function (
    groupObjectId: number,
    membersSortFieldId: number,
    templateId: number,
    useSecondarySize: boolean,
    partitionsMemberObjects: TObjectId[],
    adjustedSize: number,
    mandatoryFieldValues: TFieldValue[],
    callback: (result: IPartitionObjectParamResponse["parameters"][0]) => void = _.noop,
    errorCallback: (result: TAPIStatusClass) => void = _.noop
) {
    const data: TPartitionObjectParams = {
        groupObjectId,
        useSizeFieldSecond: useSecondarySize,
        partitionsMemberObjects: [partitionsMemberObjects],
        partitionsSize: [adjustedSize],
        partitionsFields: [
            mandatoryFieldValues.map((field) => ({ class: "fieldvalue", ...field })),
        ],
        membersSortFieldId,
        partitionsExtId: [""], // Will be auto generated. We don't currently want the user to have to enter an extId in the UI.
        templateId,
    };

    if (partitionsMemberObjects?.length === 0) {
        delete data.partitionsMemberObjects;
    } else {
        delete data.partitionsSize;
    }

    dispatcher.send(
        "partitionObject",
        data,
        (result: IPartitionObjectParamResponse) => {
            if (result.parameters[0]?.length) {
                callback(result.parameters[0]);
            } else {
                errorCallback(result.parameters[1]);
            }
        },
        false,
        (status: string) => {
            errorCallback({ class: "status", details: status, message: "" });
        }
    );
};

const deleteActivityInfoFromReservations = function (
    activityIds: string[],
    callback: (
        cleared: TReservationIdClass[],
        failed: TReservationIdClass[],
        statuses: TAPIStatusClass[]
    ) => void
) {
    dispatcher.send(
        "deleteActivityInfoFromReservations",
        { activityIds },
        (result: IDeleteActivityInfoResponse) => {
            // successful reservation ids {reservationId objects}
            // failed reservation ids { reservationId objects}
            // status list - one per failed reservation
            callback(result.parameters[0], result.parameters[1], result.parameters[2]);
        }
    );
};

const getPartitionObjects = function (
    reservationId: number,
    useSizeFieldSecond: boolean,
    callback: (status: TAPIStatusClass, objects: TObjectIdClass[]) => void
) {
    dispatcher.send(
        "getPartitionObjects",
        { reservationId: { class: "reservationid", id: reservationId }, useSizeFieldSecond },
        (result: IGetPartitionObjectsResponse) => {
            console.log(result.parameters);
            // 0: Status array, 1: Object array
            callback(result.parameters[0], result.parameters[1]);
        }
    );
};

const useDualSizeSum = function (callback: (useDual: boolean) => void) {
    dispatcher.send(
        "useDualSizeSum",
        {},
        (result: IUseDualSizeSumResponse) => {
            callback(result.parameters[0] || false);
        },
        false,
        (status, message, response) => {
            callback(false);
        }
    );
};

const getPartitionMemberType = (
    groupObjectId: number,
    templateId: number,
    useSizeSecond: boolean,
    callback: (status: IGetPartitionMemberTypeResponse["parameters"][0], typeId: number) => void
) => {
    dispatcher.send(
        "getPartitionMemberType",
        {
            groupObjectId: { class: "objectid", id: groupObjectId },
            templateId: { class: "templateid", id: templateId },
            useSizeSecond,
        },
        (result: IGetPartitionMemberTypeResponse) => {
            callback(result.parameters[0], result.parameters[1]?.id || 0);
        }
    );
};

const getPartitionFields = (
    templateId: number,
    useSizeFieldSecond: boolean,
    callback: (result: number[]) => void
) => {
    dispatcher.send(
        "getPartitionFields",
        {
            templateId: { class: "templateid", id: templateId },
            useSizeFieldSecond,
        },
        (result: IGetPartitionFieldsResponse) => {
            callback(result.parameters[0].map((fld) => fld.id));
        }
    );
};

/*
 * Unused (could be deleted).
 */

// let entryPropertyDefs = [];
// let versionInfo = null;

// const checkPersistReservationLayer = function (layerId, callback) {
//     dispatcher.send("checkPersistReservationLayer", { layerId }, (result) => {
//         try {
//             callback(result.parameters);
//         } catch (error) {
//             console.error("Error in checkPersistReservationLayer", error);
//         }
//     });
// };

// const deleteOrders = function (orderIds: number[], callback: (status: TAPIStatus[]) => void) {
//     dispatcher.send(
//         "deleteOrders",
//         {
//             orderIds: orderIds.map((oId) => ({ class: "orderid", id: oId })),
//         },
//         (result: TImportOrdersResult) => {
//             callback(result.parameters[0]);
//         }
//     );
// };

// const deleteTag = (tag, callback) => {
//     dispatcher.send("deleteTag", { tag }, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in deleteTag", error);
//         }
//     });
// };

// const findOrders = function (query, callback) {
//     dispatcher.send("findOrders", query, callback);
// };

// const findReservations = function (query, callback) {
//     dispatcher.send("findReservations", query, callback);
// };

// const getAllAuthServers = function (client, database, callback, timeoutFunction) {
//     dispatcher.send("getAllAuthServers", { client, database }, callback, timeoutFunction);
// };

// const getAllEntryPropertyDefs = function (callback) {
//     if (entryPropertyDefs && entryPropertyDefs.length > 0) {
//         callback(entryPropertyDefs);
//     } else {
//         dispatcher.send("getAllEntryPropertyDefs", {}, (result) => {
//             try {
//                 entryPropertyDefs = result.parameters[0];
//                 callback(entryPropertyDefs);
//             } catch (error) {
//                 console.error("Error in getAllEntryPropertyDefs", error);
//             }
//         });
//     }
// };

// const getAuthServers = function (client, database, callback, timeoutFunction) {
//     dispatcher.send("getAuthServers", { client, database }, callback, timeoutFunction);
// };

// const getColorDefs = function (callback) {
//     dispatcher.send("getColorDefs", { objectColorsOnly: false }, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in getColorDefs", error);
//         }
//     });
// };

// const getDatabases = function (client, callback) {
//     dispatcher.send("getAllDatabases", { client }, callback);
// };

// const getOrders = function (orderIds, callback) {
//     dispatcher.send("getOrders", { orderIds, createObjectsWithNames: true }, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in getOrders", error);
//         }
//     });
// };

// const getPeriods = function (periodIds, callback) {
//     dispatcher.send("getPeriods", { periodIds }, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in getPeriods", error);
//         }
//     });
// };

// const getServers = function (callback) {
//     dispatcher.send("getServers", {}, callback);
// };

// const getSpecialFields = function (callback) {
//     dispatcher.send("getSpecialFields", {}, (result) => callback(result.parameters));
// };

// const getSpecialTypes = function (callback) {
//     dispatcher.send("getSpecialTypes", {}, (result) => callback(result.parameters));
// };

// const getSystemBackgroundText = function (callback) {
//     dispatcher.send("getSystemBackgroundText", {}, (result) => callback(result.parameters[0]));
// };

// const getTimezonesList = function (callback) {
//     dispatcher.send("getTimeZonesList", {}, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in getTimezonesList", error);
//         }
//     });
// };

// const getVersionInfo = function (callback) {
//     if (versionInfo !== null) {
//         callback(versionInfo);
//         return;
//     }
//
//     dispatcher.send("getVersionInfo", {}, (result) => {
//         try {
//             versionInfo = result.parameters[0];
//             callback(versionInfo);
//         } catch (error) {
//             console.error("Error in getVersionInfo", error);
//         }
//     });
// };

// const importReservations = (reservations, callback) => {
//     dispatcher.send("importReservations", { reservations }, (result) => {
//         callback(result.parameters[0]);
//     });
// };

// const supportsEmail = function (callback) {
//     dispatcher.send("supportsEmail", {}, (result) => {
//         try {
//             return callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in supportsEmail", error);
//             return;
//         }
//     });
// };

// const supportsReservationExceptions = function (callback) {
//     dispatcher.send("supportsReservationExceptions", {}, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in supportsReservationExceptions", error);
//         }
//     });
// };

// const targetExternalAppsOnStaging = (callback) => {
//     dispatcher.send("targetExternalAppsOnStaging", {}, (result) => {
//         try {
//             callback(result.parameters[0]);
//         } catch (error) {
//             console.error("Error in targetExternalAppsOnStaging", error);
//         }
//     });
// };

// const updatePassword = function (oldPassword, newPassword, callback) {
//     dispatcher.send("updatePassword", { oldPassword, newPassword }, callback);
// };

const API = {
    getLimitation,
    getLanguages,
    getTemplateGroups,
    findReservationsList,
    findAllReservationsList,
    findReservationsCancelledList,
    okToCancelReservations,
    okToChangeViews,
    okToPublishView,
    exportOrders,
    importOrders,
    deleteOrderRows,
    getReservations,
    getReservationsByExtid,
    getReservationsCancelled,
    getReservationsHistory,
    exportReservations,
    cancelReservation,
    restoreReservations,
    restoreReservationsFromHistory,
    getModifiableReservationFields,
    setModifiableReservationFields,
    findEntries,
    findTypes,
    findAliasTypes,
    findFields,
    getAllFields,
    getFields,
    getFieldsByExtid,
    getFieldDefs,
    getAllFieldDefs,
    findCategories,
    setPrimaryField,
    getPrimaryFields,
    getObjects,
    getObjectsByExtid,
    getObjectNames,
    getObjectNamesByExtid,
    getObjectsLimited,
    getObjectsWithFieldValues,
    getAssociatedTypes,
    getAssociatedObjects,
    getSuggestedObjectsSimple,
    getSuggestedObjectsAdvanced,
    getSuggestedObjectsAdvanced2,
    deleteFromSuggestedObjects,
    findObjects,
    exportObjects,
    exportObjectsBatch,
    importObjects,
    importObjectsBatch,
    createEditableObject,
    deleteObjects,
    getTypeDefsExtended,
    getTypes,
    getTypesByExtid,
    getRelatedTypes,
    getCreateTypes,
    getCreateTypesKinds,
    findNextCreateTypes,
    getTypeTree,
    updateMcFluffy,
    addToMcFluffy,
    addToMcFluffyMulti,
    addToMcFluffyServer,
    setToMcFluffy,
    setReservationToMcFluffy,
    setOrderRowToMcFluffy,
    removeFromMcFluffy,
    replaceInMcFluffy,
    reserveMcFluffy,
    getUserLocale,
    getCurrentUserPrefs,
    setCurrentUserPrefs,
    getObjectTemplate,
    findUsers,
    getUsers,
    getOrganizations,
    findHPeriods,
    getHPeriodDefs,
    setPreferences,
    setPreferencesWithRefs,
    setDefaultPreferences,
    getPreferences,
    getPreferencesMulti,
    getOtherUserPrefs,
    getStartupData,
    getSupportInfo,
    getEmailReservationPrefs,
    sendEmailReservation,
    findOrderRowsList,
    getOrderRows,
    getAuthenticatedUserInfo,
    getFieldDefsForType,
    getPrimaryFieldDefsForType,
    getFieldDefsForReservations,
    findReservationFields,
    getFieldsForReservationLists,
    findFieldsForTypes,
    getModifiableObjectFields,
    moveReservationsInTime,
    copyReservationsInTime,
    replaceObjectsOnReservations,
    dayNumberFromString,
    reservationClusterChangeTime,
    getValidReservationStatus,
    getReservationStatus,
    setReservationStatus,
    createReservationLayer,
    deleteReservationLayer,
    addToReservationLayer,
    deleteFromReservationLayer,
    persistReservationLayer,
    getReservationLayers,
    hasExternalPermissions,
    getReservationCollisions,
    getReservationsOnLayer,
    renameReservationLayer,
    groupReservations,
    getGroupedEntries,
    findSumSettings,
    getSumSetting,
    setSumSetting,
    deleteSumSetting,
    getColorTypes,
    getTypesOnReservations,
    getObjectsOnReservations,
    getReservableTypesForType,
    getReservationsWithObjects,
    getReservationsWithTime,
    findPeriods,
    okToSetReservationOrganizations,
    setReservationOrganizations,
    getTimezones,
    getCurrentDateTime,
    okToModifyObjects,
    okToModifyOrders,
    getMemberTypesForReservations,
    getMemberObjectsForReservations,
    getMemberExceptionObjectsForReservations,
    saveExceptionObjectsForReservations,
    canHaveMemberExceptionObjectsForReservations,
    findObjectFieldsForReservationList,
    getObjectCategories,
    moveReservationsFromWaitingList,
    moveReservationsToWaitingList,
    showEnableCustomWeekNames,
    getCustomWeekNames,
    targetExternalAppsEnvironment,
    getUserSearchFields,
    setUserSearchFields,
    addTagToReservations,
    removeTagFromReservations,
    createTag,
    findTags,
    findMyTags,
    okToDeleteReservationsCancelled,
    deleteReservationsCancelled,
    getReservationConflicts,
    getOrgInfo,
    getExamSettings,
    getServerEnv,
    loginWithSSOToken,
    logoutUser,
    addReservationsToReservationGroup,
    removeReservationsFromReservationGroup,
    createReservationGroup,
    createReservationGroupWithReservations,
    updateReservationGroup,
    deleteReservationGroup,
    getReservationGroup,
    getPaddingRules,
    clearAmCaches,
    lockReservations,
    unlockReservations,
    setReservationCapacity,
    partitionObject,
    deleteActivityInfoFromReservations,
    getPartitionObjects,
    useDualSizeSum,
    getPartitionMemberType,
    getPartitionFields,
    MASS_BATCH_SIZE,
    STATUS_BATCH_SIZE,

    sendMessage(...args: Parameters<typeof dispatcher.send>) {
        dispatcher.send(...args);
    },

    setMessageDispatcher(newDispatcher: MessageDispatcher, newLogger: typeof logger) {
        dispatcher = newDispatcher;
        logger = newLogger;
    },

    getMessageDispatcher() {
        return dispatcher;
    },
};

export default API;
